import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, fileUrl, apiError, setActiveSpace } from "../lib/api";
import { useConfirm } from "./ConfirmDialog";
import { dist, PLACEHOLDER_BG, toCsv, parseCsv, neighbourBBox, fenceLengthM } from "../lib/helpers";
import { buildImportPlan, normName } from "../lib/csvImport";
import { exportMapPNG, exportMapPDF } from "../lib/mapExport";
import { useAuth } from "../context/AuthContext";
import { useGeolocation } from "../hooks/useGeolocation";
import { computeGeoTransform, computeCalibrationQuality } from "../lib/geo";
import Canvas from "./Canvas";
import MobileView from "./MobileView";
import { useIsMobile } from "../hooks/useIsMobile";
import PropertyPanel from "./PropertyPanel";
import CategoryManager from "./CategoryManager";
import PlacePanel from "./PlacePanel";
import GuardPanel from "./GuardPanel";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  MapPin, LogOut, Lock, MousePointer2, PlusSquare, Ruler, Upload, Search, Zap, Move, SquareDashedMousePointer, Download, FileUp, Save, RotateCcw, Trash2, AlertTriangle, Layers, Undo2, ChevronLeft, ChevronRight, ChevronDown, FileText, Image as ImageIcon, Fence, Moon, Shield, Copy, ClipboardList, Grid3x3, LocateFixed, Navigation, Satellite, CalendarClock, EyeOff,
} from "lucide-react";

export default function MapPlanner() {
  const { isAdmin, isSuperAdmin, isTeam, isAuthenticated, loading: authLoading, login, logout } = useAuth();
  const isMobile = useIsMobile();

  const [mode, setMode] = useState("view");

  const [categories, setCategories] = useState([]);
  const [elements, setElements] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [activeSpaceId, setActiveSpaceId] = useState(null);
  const [totals, setTotals] = useState(null);
  const [editingSpaceId, setEditingSpaceId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [map, setMap] = useState({ background_path: null, pixels_per_meter: 50 });
  const confirm = useConfirm();

  const [selectedId, setSelectedId] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);
  const [tool, setTool] = useState("select");
  const [placeCatId, setPlaceCatId] = useState("");

  const [calibPoints, setCalibPoints] = useState([]);
  const [calibOpen, setCalibOpen] = useState(false);
  const [calibMeters, setCalibMeters] = useState("");

  // view filters
  const [hiddenCats, setHiddenCats] = useState([]); // category ids the user unchecked in the view filter
  const [catMenuOpen, setCatMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showLabelsOnly, setShowLabelsOnly] = useState(false);
  const [showConsumption, setShowConsumption] = useState(false);
  const [showConsumptionNight, setShowConsumptionNight] = useState(false);
  const [showMissingConsumption, setShowMissingConsumption] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [gridStep, setGridStep] = useState(5);
  const [gridLocked, setGridLocked] = useState(false);
  const [gridShiftX, setGridShiftX] = useState(0);
  const [bandResult, setBandResult] = useState(null);
  const [viewDrag, setViewDrag] = useState("pan"); // 'pan' | 'select'
  const [backups, setBackups] = useState([]);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [backgroundOpen, setBackgroundOpen] = useState(false);
  const [placeOpen, setPlaceOpen] = useState(false);
  const [placeConfig, setPlaceConfig] = useState({ category_id: "", name: "Element nou", length: 2, width: 2, consumption: 0, consumption_night: 0, rotation: 0, mobilier: "", branding: "", altele: "" });
  const [guardOpen, setGuardOpen] = useState(false);
  const [guardConfig, setGuardConfig] = useState({ name: "Punct pază", agents_setup: 0, agents_day: 0, agents_event: 0, agents_night: 0 });
  const [guardMode, setGuardMode] = useState("day"); // 'setup' | 'day' | 'night'
  const [fenceMode, setFenceMode] = useState("large"); // 'large' | 'small'
  const [importPreview, setImportPreview] = useState(null);
  const [dupPreview, setDupPreview] = useState(null);
  const [lastImportBackup, setLastImportBackup] = useState(null);
  const [adminSearch, setAdminSearch] = useState("");
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const ppm = map.pixels_per_meter || 50;
  const labelLod = map.label_lod || 26;
  const bg = map.background_path ? fileUrl(map.background_path) : PLACEHOLDER_BG;

  useEffect(() => { if (!isAdmin) setMode("view"); }, [isAdmin]);

  const load = async (preferSpaceId) => {
    const [c, sp, m] = await Promise.all([
      api.get("/categories"), api.get("/spaces"), api.get("/map"),
    ]);
    setCategories(c.data);
    const list = sp.data || [];
    setSpaces(list);
    const active = list.find((s) => s.id === (preferSpaceId || activeSpaceId)) || list[0];
    const aid = active ? active.id : null;
    setActiveSpaceId(aid);
    setActiveSpace(aid);
    const e = await api.get("/elements", { params: aid ? { space_id: aid } : {} });
    setElements(e.data);
    setMap({ ...(active || {}), event_name: m.data.event_name, event_location: m.data.event_location, event_date: m.data.event_date });
    if (!placeCatId && c.data[0]) setPlaceCatId(c.data[0].id);
  };

  const switchSpace = async (id) => {
    if (!id || id === activeSpaceId) return;
    setActiveSpaceId(id);
    setActiveSpace(id);
    setSelectedId(null);
    const sp = spaces.find((s) => s.id === id) || {};
    try {
      const e = await api.get("/elements", { params: { space_id: id } });
      setElements(e.data);
    } catch { setElements([]); }
    setMap((m) => ({ ...m, background_path: sp.background_path, pixels_per_meter: sp.pixels_per_meter, label_lod: sp.label_lod, geo_points: sp.geo_points || [] }));
  };

  const fetchTotals = async () => {
    if (!isTeam) { setTotals(null); return; }
    try { const { data } = await api.get("/totals"); setTotals(data); } catch { /* ignore */ }
  };
  useEffect(() => { fetchTotals(); /* eslint-disable-next-line */ }, [elements, spaces, isTeam]);
  // keep the spaces[] cache in sync with map edits to the active space
  useEffect(() => {
    if (!activeSpaceId) return;
    setSpaces((prev) => prev.map((s) => s.id === activeSpaceId
      ? { ...s, background_path: map.background_path, pixels_per_meter: map.pixels_per_meter, label_lod: map.label_lod, geo_points: map.geo_points }
      : s));
    /* eslint-disable-next-line */
  }, [map.background_path, map.pixels_per_meter, map.label_lod, map.geo_points, activeSpaceId]);

  const addSpace = async () => {
    try {
      const { data } = await api.post("/spaces", { name: `Zona ${spaces.length + 1}` });
      setSpaces((p) => [...p, data]);
      switchSpace(data.id);
      toast.success("Spațiu adăugat");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Eroare"); }
  };
  const deleteSpace = async (id) => {
    if (spaces.length <= 1) { toast.error("Nu poți șterge ultimul spațiu"); return; }
    const sp = spaces.find((s) => s.id === id);
    if (!(await confirm({ title: "Șterge spațiu", danger: true, confirmText: "Șterge spațiul",
      description: `Ștergi „${sp.name}" și TOATE elementele lui? Acțiune ireversibilă.` }))) return;
    try {
      await api.delete(`/spaces/${id}`);
      const rest = spaces.filter((s) => s.id !== id);
      setSpaces(rest);
      if (activeSpaceId === id) switchSpace(rest[0].id);
      toast.success("Spațiu șters");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Eroare"); }
  };
  const moveSpace = async (id, dir) => {
    const idx = spaces.findIndex((s) => s.id === id);
    const j = idx + dir;
    if (j < 0 || j >= spaces.length) return;
    const arr = [...spaces];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSpaces(arr);
    try { await api.post("/spaces/reorder", { order: arr.map((s) => s.id) }); } catch {}
  };
  const renameSpace = async (id, name) => {
    setEditingSpaceId(null);
    const sp = spaces.find((s) => s.id === id);
    if (!sp || !name.trim() || name.trim() === sp.name) return;
    try {
      await api.patch(`/spaces/${id}`, { name: name.trim() });
      setSpaces((p) => p.map((s) => (s.id === id ? { ...s, name: name.trim() } : s)));
      if (activeSpaceId === id) setMap((m) => ({ ...m }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Eroare"); }
  };

  useEffect(() => {
    if (authLoading) return; // wait for Auth0 to resolve so the token (if any) is attached
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isTeam]);

  // ---- backups ----
  const loadBackups = async () => {
    try { const { data } = await api.get("/backups"); setBackups(data); }
    catch (error) { console.error("Nu am putut încărca backup-urile:", error); }
  };
  useEffect(() => { if (isSuperAdmin) loadBackups(); }, [isSuperAdmin]);

  const createBackup = async (silent = false) => {
    try {
      const { data } = await api.post("/backups", {});
      await loadBackups();
      if (!silent) toast.success("Backup salvat");
      return data;
    } catch { if (!silent) toast.error("Backup eșuat"); return null; }
  };

  const restoreBackup = async (id) => {
    try {
      const { data } = await api.post(`/backups/${id}/restore`);
      await load();
      await loadBackups();
      setSelectedId(null);
      toast.success(`Backup încărcat (${data.restored} elemente)`);
    } catch { toast.error("Încărcare backup eșuată"); }
  };

  const deleteBackup = async (id) => {
    await api.delete(`/backups/${id}`);
    setBackups((p) => p.filter((b) => b.id !== id));
  };

  const exitAdmin = async () => {
    setPlaceOpen(false);
    // flush any pending debounced save and persist the latest selected element
    if (commitTimer.current) { clearTimeout(commitTimer.current); commitTimer.current = null; }
    const cur = selectedId ? elementsRef.current.find((e) => e.id === selectedId) : null;
    if (cur) await putElement(cur);
    await createBackup(true);
    setMode("view");
    setTool("select");
    setSelectedId(null);
  };

  const handleLogout = async () => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    if (isAdmin) {
      try { await createBackup(true); } catch { /* ignore */ }
    }
    logout();
  };

  const selected = elements.find((el) => el.id === selectedId) || null;

  // Selecting an element (any mode): zoom the view onto it and its neighbours.
  const handleSelect = (id) => {
    setSelectedId(id);
    if (id) {
      const bbox = neighbourBBox(elements, id, ppm);
      if (bbox) setFocusRequest({ bbox, ts: Date.now() });
    }
  };

  const isHiddenEl = (el) => {
    if (el.kind === "fence") return hiddenCats.includes(el.fence_size === "small" ? "fence-small" : "fence-large");
    return hiddenCats.includes(el.category_id);
  };

  const adminFiltered = elements.filter((el) =>
    (el.name || "").toLowerCase().includes(adminSearch.trim().toLowerCase()) &&
    !isHiddenEl(el)
  );

  // always-current ref so commits during a drag never read a stale closure
  const elementsRef = useRef(elements);
  useEffect(() => { elementsRef.current = elements; }, [elements]);

  // delete the selected element with the Delete key (edit mode only)
  useEffect(() => {
    if (mode !== "admin") return;
    const onKey = (e) => {
      if (e.key !== "Delete") return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (t && t.isContentEditable)) return;
      if (!selectedId) return;
      e.preventDefault();
      deleteSelected();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedId]);

  const visibleElements = useMemo(() => {
    const catFiltered = elements.filter((el) => !isHiddenEl(el));
    if (mode === "admin") return catFiltered;
    return catFiltered.filter(
      (el) => !search || el.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [elements, mode, hiddenCats, search]);

  // ---- element CRUD ----
  const placeElement = async (world) => {
    const c = placeConfig;
    if (!c || !c.category_id) return;
    const body = {
      name: c.name || "Element nou", category_id: c.category_id,
      length: c.length, width: c.width, consumption: c.consumption || 0, consumption_night: c.consumption_night || 0, rotation: c.rotation || 0,
      mobilier: c.mobilier || "", branding: c.branding || "", altele: c.altele || "",
      x: world.x - (c.length * ppm) / 2, y: world.y - (c.width * ppm) / 2,
    };
    const { data } = await api.post("/elements", body);
    setElements((p) => [...p, data]);
    setSelectedId(data.id);
    // keep the panel open so multiple elements can be placed
  };

  const openPlace = () => {
    const cat = categories.find((c) => c.id !== "fence-cat") || categories[0];
    if (!cat) { toast.error("Creează întâi o categorie"); return; }
    setPlaceConfig({ category_id: cat.id, name: "Element nou", length: cat.length, width: cat.width, consumption: 0, consumption_night: 0, rotation: 0, mobilier: "", branding: "", altele: "" });
    setTool("place");
    setPlaceOpen(true);
  };

  // ---- fences (polyline elements) ----
  const fences = elements.filter((e) => e.kind === "fence");
  const fencesLarge = fences.filter((e) => (e.fence_size || "large") !== "small");
  const fencesSmall = fences.filter((e) => e.fence_size === "small");
  // filter dropdown entries: replace the single "fence-cat" with two size options
  const filterCats = useMemo(() => {
    const out = [];
    let added = false;
    for (const c of categories) {
      if (c.id === "fence-cat") {
        out.push({ id: "fence-large", name: "Garduri mari", color: "#FF0000" });
        out.push({ id: "fence-small", name: "Garduri mici", color: "#F97316" });
        added = true;
      } else out.push(c);
    }
    if (!added) {
      out.push({ id: "fence-large", name: "Garduri mari", color: "#FF0000" });
      out.push({ id: "fence-small", name: "Garduri mici", color: "#F97316" });
    }
    return out;
  }, [categories]);
  const consumElements = elements.filter((e) => e.kind !== "fence" && e.kind !== "guard");
  const missingConsumptionCount = consumElements.filter((e) => (Number(e.consumption) || 0) === 0).length;
  const totalConsumptionDay = consumElements.reduce((s, e) => s + (Number(e.consumption) || 0), 0);
  const totalConsumptionNight = consumElements.reduce((s, e) => s + (Number(e.consumption_night) || 0), 0);
  const totalFenceM = fences.reduce((s, f) => s + fenceLengthM(f.points, ppm), 0);
  const totalFenceMLarge = fencesLarge.reduce((s, f) => s + fenceLengthM(f.points, ppm), 0);
  const totalFenceMSmall = fencesSmall.reduce((s, f) => s + fenceLengthM(f.points, ppm), 0);
  const nextFenceName = (size) => {
    const label = size === "small" ? "Gard mic" : "Gard mare";
    const list = size === "small" ? fencesSmall : fencesLarge;
    const nums = list.map((f) => { const m = /(\d+)/.exec(f.name || ""); return m ? +m[1] : 0; });
    return `${label} ${(nums.length ? Math.max(...nums) : 0) + 1}`;
  };
  const onFenceCommit = async (points) => {
    const size = tool === "fence_small" ? "small" : "large";
    const body = {
      name: nextFenceName(size), category_id: "fence-cat", kind: "fence", fence_size: size, points,
      length: 0, width: 0, consumption: 0, consumption_night: 0, rotation: 0,
      x: points[0].x, y: points[0].y, mobilier: "", branding: "", altele: "",
    };
    try {
      const { data } = await api.post("/elements", body);
      setElements((p) => [...p, data]);
      setSelectedId(data.id);
      setTool("select");
      toast.success(`${data.name} adăugat (${fenceLengthM(points, ppm).toFixed(1)} m)`);
    } catch (e) { toast.error("Nu am putut salva gardul"); }
  };
  const onFencePointsChange = (id, points) => {
    setElements((p) => {
      const next = p.map((el) => (el.id === id ? { ...el, points, x: points[0].x, y: points[0].y } : el));
      elementsRef.current = next;
      return next;
    });
  };
  const onFencePointsCommit = async (id, points) => {
    const el = elementsRef.current.find((e) => e.id === id);
    if (!el) return;
    const updated = { ...el, points, x: points[0].x, y: points[0].y };
    setElements((p) => p.map((e) => (e.id === id ? updated : e)));
    await putElement(updated);
  };

  const closePlace = () => { setPlaceOpen(false); setTool("select"); };

  // ---- guard points (shield markers with agent counts) ----
  const guards = elements.filter((e) => e.kind === "guard");
  const guardTotal = (m) => guards.reduce((s, g) => s + (Number(g[`agents_${m}`]) || 0), 0);
  const guardActive = (m) => guards.filter((g) => (Number(g[`agents_${m}`]) || 0) > 0).length;
  const nextGuardName = () => {
    const nums = guards.map((g) => { const m = /(\d+)/.exec(g.name || ""); return m ? +m[1] : 0; });
    return `Punct pază ${(nums.length ? Math.max(...nums) : 0) + 1}`;
  };
  const openGuardPlace = () => {
    setGuardConfig({ name: nextGuardName(), agents_setup: 0, agents_day: 0, agents_event: 0, agents_night: 0 });
    setTool("guard");
    setGuardOpen(true);
  };
  const closeGuardPlace = () => { setGuardOpen(false); setTool("select"); };
  const placeGuard = async (world) => {
    const c = guardConfig;
    const body = {
      name: c.name || "Punct pază", category_id: "guard-cat", kind: "guard",
      length: 0, width: 0, consumption: 0, consumption_night: 0, rotation: 0,
      x: world.x, y: world.y, mobilier: "", branding: "", altele: "",
      agents_setup: c.agents_setup || 0, agents_day: c.agents_day || 0, agents_event: c.agents_event || 0, agents_night: c.agents_night || 0,
    };
    try {
      const { data } = await api.post("/elements", body);
      setElements((p) => [...p, data]);
      setSelectedId(data.id);
      toast.success(`${data.name} adăugat`);
    } catch (e) { toast.error("Nu am putut salva punctul de pază"); }
  };
  const guardModeLabel = { setup: "Set-up", day: "Zi", event: "Event", night: "Noapte" };
  const [guardScheduleOpen, setGuardScheduleOpen] = useState(false);
  const [projectForm, setProjectForm] = useState({ event_name: "", event_location: "", event_date: "" });
  const [savingProject, setSavingProject] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBackupDone, setResetBackupDone] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetForm, setResetForm] = useState({ event_name: "", event_location: "", event_date: "" });
  const [resetting, setResetting] = useState(false);
  useEffect(() => {
    setProjectForm({ event_name: map.event_name || "", event_location: map.event_location || "", event_date: map.event_date || "" });
  }, [map.event_name, map.event_location, map.event_date]);
  const saveProject = async () => {
    setSavingProject(true);
    const tid = toast.loading("Se salvează detaliile proiectului…");
    try {
      const { data } = await api.post("/project", projectForm);
      setMap((m) => ({ ...m, event_name: data.event_name, event_location: data.event_location, event_date: data.event_date }));
      toast.success("Detalii proiect salvate", { id: tid });
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Salvare eșuată", { id: tid }); }
    setSavingProject(false);
  };
  const openReset = () => {
    setResetForm({ event_name: "", event_location: "", event_date: "" });
    setResetBackupDone(false); setResetConfirmText(""); setResetOpen(true);
  };
  const downloadFullBackupForReset = async () => {
    try {
      await exportAll();
      setResetBackupDone(true);
      toast.success("Backup complet descărcat. Poți continua resetarea.");
    } catch (e) { toast.error("Backup-ul a eșuat. Nu poți reseta fără backup."); }
  };
  const doReset = async () => {
    setResetting(true);
    const tid = toast.loading("Se resetează proiectul…");
    try {
      const { data } = await api.post("/reset-project", resetForm);
      toast.success(`Proiect nou creat: ${data.event_name}`, { id: tid });
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Reset eșuat", { id: tid });
      setResetting(false);
    }
  };
  const [userMgmtOpen, setUserMgmtOpen] = useState(false);
  const [userForm, setUserForm] = useState({ email: "", password: "", role: "team" });
  const [userBusy, setUserBusy] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [usersList, setUsersList] = useState(null); // null=loading, []=empty, [..]=data
  const [usersError, setUsersError] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const loadUsers = async () => {
    setUsersList(null); setUsersError("");
    try {
      const { data } = await api.get("/users");
      setUsersList(data.users || []);
    } catch (e) {
      setUsersList([]);
      setUsersError(apiError(e.response?.data?.detail) || "Nu am putut încărca userii");
    }
  };
  const deleteUser = async (u) => {
    if (!(await confirm({ title: "Șterge user", danger: true, confirmText: "Șterge userul",
      description: `Sigur ștergi userul ${u.email}?\n\nAcțiunea este permanentă și userul nu se va mai putea autentifica.` }))) return;
    const tid = toast.loading("Se șterge userul…");
    try {
      await api.delete("/users", { params: { user_id: u.user_id } });
      toast.success(`User șters: ${u.email}`, { id: tid });
      loadUsers();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Ștergere eșuată", { id: tid });
    }
  };
  const fmtLastLogin = (v) => {
    if (!v) return "niciodată";
    const d = new Date(v);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" });
  };
  const checkUserExists = async () => {
    const email = userForm.email.trim().toLowerCase();
    if (!email.includes("@")) { setUserExists(null); return; }
    try {
      const { data } = await api.get("/users/lookup", { params: { email } });
      setUserExists(data);
    } catch { setUserExists(null); }
  };
  const submitUser = async () => {
    const email = userForm.email.trim().toLowerCase();
    if (!email.includes("@")) { toast.error("Email invalid"); return; }
    if ((userForm.password || "").length < 8) { toast.error("Parola trebuie să aibă minim 8 caractere"); return; }
    if (userExists?.exists) {
      if (!(await confirm({ title: "Suprascrie user", danger: true, confirmText: "Suprascrie",
        description: `Userul ${email} există deja.\n\nÎi voi SUPRASCRIE parola și rolul (rol nou: ${userForm.role}). Rolurile team/admin vechi sunt înlocuite. Continui?` }))) return;
    }
    setUserBusy(true);
    const tid = toast.loading("Se salvează userul…");
    try {
      const { data } = await api.post("/users/upsert", { email, password: userForm.password, role: userForm.role });
      toast.success(`${data.created ? "User creat" : "User actualizat"}: ${data.email} · rol ${data.role}`, { id: tid });
      setUserForm({ email: "", password: "", role: "team" });
      setUserExists(null);
      loadUsers();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Salvare eșuată", { id: tid });
    }
    setUserBusy(false);
  };
  const guardShifts = [["agents_setup", "Set-up"], ["agents_day", "Zi"], ["agents_event", "Event"], ["agents_night", "Noapte"]];
  const exportGuardScheduleCsv = () => {
    const headers = ["Punct pază", "Set-up", "Zi", "Event", "Noapte", "Total"];
    const rows = guards.map((g) => {
      const su = Number(g.agents_setup) || 0, d = Number(g.agents_day) || 0, ev = Number(g.agents_event) || 0, ni = Number(g.agents_night) || 0;
      return { "Punct pază": g.name || "", "Set-up": su, "Zi": d, "Event": ev, "Noapte": ni, "Total": su + d + ev + ni };
    });
    rows.push({
      "Punct pază": "TOTAL", "Set-up": guardTotal("setup"), "Zi": guardTotal("day"),
      "Event": guardTotal("event"), "Noapte": guardTotal("night"),
      "Total": guardTotal("setup") + guardTotal("day") + guardTotal("event") + guardTotal("night"),
    });
    const csv = toCsv(headers, rows);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `program-paza-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Program pază exportat (${guards.length} puncte)`);
  };


  const commitTimer = useRef(null);

  const putElement = async (el) => {
    const { id, ...payload } = el;
    try {
      await api.put(`/elements/${id}`, payload);
    } catch (e) {
      if (e.response?.status !== 401) toast.error("Salvare eșuată");
    }
  };

  const dragElement = (id, x, y) =>
    setElements((p) => {
      const next = p.map((el) => (el.id === id ? { ...el, x, y } : el));
      elementsRef.current = next;
      return next;
    });

  const commitElement = async (id, x, y) => {
    const target = elementsRef.current.find((e) => e.id === id);
    if (!target) return;
    const final = (x != null && y != null) ? { ...target, x, y } : target;
    await putElement(final);
  };

  const changeSelected = (next) => {
    setElements((p) => p.map((el) => (el.id === next.id ? next : el)));
    // robust auto-save: persist shortly after the last change (independent of blur)
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => putElement(next), 500);
  };

  const rotateSelected = () => {
    if (!selected) return;
    const next = { ...selected, rotation: (selected.rotation + 15) % 360 };
    changeSelected(next);
    putElement(next);
  };

  const duplicateSelected = async () => {
    if (!selected) return;
    const { id, ...rest } = selected;
    const body = { ...rest, name: rest.name + " (copie)", x: rest.x + 20, y: rest.y + 20 };
    const { data } = await api.post("/elements", body);
    setElements((p) => [...p, data]);
    setSelectedId(data.id);
    toast.success("Element copiat");
  };

  const deleteSelected = async () => {
    if (!selected) return;
    await api.delete(`/elements/${selected.id}`);
    setElements((p) => p.filter((el) => el.id !== selected.id));
    setSelectedId(null);
  };

  // ---- categories ----
  const createCategory = async (body) => {
    const { data } = await api.post("/categories", body);
    setCategories((p) => [...p, data]);
  };
  const updateCategory = async (id, body) => {
    const { id: _i, ...payload } = body;
    setCategories((p) => p.map((c) => (c.id === id ? { ...c, ...payload } : c)));
    try { await api.put(`/categories/${id}`, payload); } catch { toast.error("Salvare eșuată"); }
  };
  const deleteCategory = async (id) => {
    await api.delete(`/categories/${id}`);
    setCategories((p) => p.filter((c) => c.id !== id));
  };

  // ---- calibration ----
  const addCalibPoint = (world) => {
    setCalibPoints((p) => {
      const np = [...p, world].slice(-2);
      if (np.length === 2) setCalibOpen(true);
      return np;
    });
  };
  const confirmCalibration = async () => {
    const meters = parseFloat(calibMeters);
    if (!meters || calibPoints.length < 2) return;
    const px = dist(calibPoints[0], calibPoints[1]);
    const newPpm = px / meters;
    const body = { background_path: map.background_path, pixels_per_meter: newPpm, label_lod: labelLod };
    await api.put(`/spaces/${activeSpaceId}/map`, body);
    setMap((m) => ({ ...m, pixels_per_meter: newPpm }));
    setCalibOpen(false); setCalibPoints([]); setCalibMeters(""); setTool("select");
    toast.success(`Scară setată: ${newPpm.toFixed(1)} px/m`);
  };

  // ---- GPS location + georeferencing (3 control points) ----
  const geo = useGeolocation();
  const [showUserLoc, setShowUserLoc] = useState(false);
  const geoTransform = useMemo(() => computeGeoTransform(map.geo_points || []), [map.geo_points]);
  const geoQuality = useMemo(() => computeCalibrationQuality(map.geo_points || []), [map.geo_points]);
  const geoCalibrated = !!geoTransform;
  const userDot = useMemo(() => {
    if (!showUserLoc || !geo.coords || !geoTransform) return null;
    const p = geoTransform.latlonToPixel(geo.coords.lat, geo.coords.lon);
    const accuracyPx = (geo.coords.accuracy || 0) * (geoTransform.pxPerMeter || ppm);
    return { x: p.x, y: p.y, accuracyPx };
  }, [showUserLoc, geo.coords, geoTransform, ppm]);

  const toggleUserLoc = () => {
    if (showUserLoc) { geo.stop(); setShowUserLoc(false); return; }
    setShowUserLoc(true);
    geo.start();
  };
  const centerOnMe = () => {
    if (userDot) setFocusRequest({ x: userDot.x, y: userDot.y, ts: Date.now(), keepZoom: true });
    else toast.message("Aștept semnalul GPS…");
  };

  // GPS calibration draft flow
  const [geoCalibDraft, setGeoCalibDraft] = useState([]);
  const [geoPendingPixel, setGeoPendingPixel] = useState(null);
  const [geoCapturing, setGeoCapturing] = useState(false);
  const geoDraftQuality = useMemo(() => computeCalibrationQuality(geoCalibDraft), [geoCalibDraft]);
  const startGeoCalib = async () => {
    if (!(await confirm({ title: "Recalibrează GPS", danger: false, confirmText: "Continuă",
      description: "Sigur vrei să modifici harta? Vei recalibra poziționarea GPS." }))) return;
    setGeoCalibDraft([]); setGeoPendingPixel(null);
    setTool("geocalib"); setBackgroundOpen(false);
    toast.message("Atinge pe hartă locul unde te afli, apoi capturează GPS-ul.");
  };
  const onGeoCalibPoint = (world) => {
    if (geoCalibDraft.length >= 3) return;
    setGeoPendingPixel({ x: world.x, y: world.y });
  };
  const captureGeoPoint = async () => {
    if (!geoPendingPixel) return;
    setGeoCapturing(true);
    try {
      const c = await geo.captureOnce();
      setGeoCalibDraft((d) => [...d, { x: geoPendingPixel.x, y: geoPendingPixel.y, lat: c.lat, lon: c.lon, accuracy: c.accuracy }]);
      setGeoPendingPixel(null);
      toast.success(`Punct ${geoCalibDraft.length + 1}/3 capturat (±${Math.round(c.accuracy)} m)`);
    } catch (e) {
      toast.error(e?.code === 1 ? "Acces la locație refuzat" : "Nu am putut citi GPS-ul");
    }
    setGeoCapturing(false);
  };
  const saveGeoCalibration = async () => {
    if (geoCalibDraft.length < 3) return;
    try {
      await api.put(`/spaces/${activeSpaceId}/map`, { background_path: map.background_path, pixels_per_meter: ppm, label_lod: labelLod, geo_points: geoCalibDraft });
      setMap((m) => ({ ...m, geo_points: geoCalibDraft }));
      setGeoCalibDraft([]); setTool("select");
      toast.success("Calibrare GPS salvată");
    } catch { toast.error("Salvarea calibrării a eșuat"); }
  };
  const cancelGeoCalib = () => { setGeoCalibDraft([]); setGeoPendingPixel(null); setTool("select"); };

  // ---- label LOD (zoom threshold at which element names appear) ----
  const setLodLive = (v) => setMap((m) => ({ ...m, label_lod: v }));
  const persistLod = async () => {
    try {
      await api.put(`/spaces/${activeSpaceId}/map`, { background_path: map.background_path, pixels_per_meter: ppm, label_lod: map.label_lod || 26 });
      toast.success("Setare etichete salvată");
    } catch { toast.error("Salvare eșuată"); }
  };

  // ---- background upload ----
  const onUploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/spaces/${activeSpaceId}/map`, { background_path: data.path, pixels_per_meter: ppm, label_lod: labelLod });
      setMap((m) => ({ ...m, background_path: data.path }));
      toast.success("Fundal încărcat. Recalibrează scara dacă e nevoie.");
    } catch { toast.error("Încărcare eșuată"); }
    e.target.value = "";
  };

  // ---- CSV export / import ----
  const exportCsv = () => {
    const headers = ["id", "name", "category", "length", "width", "consumption", "consumption_night", "x", "y", "rotation", "mobilier", "branding", "altele"];
    const rows = elements.filter((el) => el.kind !== "fence" && el.kind !== "guard").map((el) => {
      const cat = categories.find((c) => c.id === el.category_id);
      return {
        id: el.id, name: el.name, category: cat?.name || "", length: el.length, width: el.width,
        consumption: el.consumption, consumption_night: el.consumption_night || 0, x: Math.round(el.x), y: Math.round(el.y), rotation: el.rotation,
        mobilier: el.mobilier || "", branding: el.branding || "", altele: el.altele || "",
      };
    });
    const csv = toCsv(headers, rows);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `harta-elemente-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} elemente exportate`);
  };

  // ---- full portable backup (JSON incl. background image) ----
  const jsonInputRef = useRef(null);
  const [importingAll, setImportingAll] = useState(false);
  const exportAll = async () => {
    const tid = toast.loading("Se pregătește backup-ul complet…");
    try {
      const { data } = await api.get("/export-all");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `blf-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup complet: ${(data.elements || []).length} elemente, ${(data.categories || []).length} categorii`, { id: tid });
    } catch {
      toast.error("Export complet eșuat", { id: tid });
    }
  };
  const onJsonSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      toast.error("Fișier JSON invalid");
      return;
    }
    const nEl = (parsed.elements || []).length;
    const nCat = (parsed.categories || []).length;
    const legacy = (parsed.elements || []).some((el) => !el.space_id);
    const spaceName = spaces.find((s) => s.id === activeSpaceId)?.name || "";
    const description = legacy
      ? `Backup vechi (fără spații) cu ${nEl} elemente. Poți:\n• ÎNLOCUI elementele, fundalul și scara spațiului „${spaceName}", sau\n• îl ADĂUGA ca spațiu nou, lângă cele existente.\n\nCelelalte spații rămân neatinse; cele ${nCat} categorii se adaugă/actualizează. Se face automat un backup de siguranță înainte.`
      : `Import complet: ${nEl} elemente și ${nCat} categorii vor ÎNLOCUI conținutul actual al hărții.\n\nSe face automat un backup de siguranță înainte. Continui?`;
    const choice = await confirm({ title: "Import complet", danger: true, description,
      confirmText: legacy ? `Înlocuiește „${spaceName}"` : "Importă și înlocuiește",
      altText: legacy ? "Adaugă ca spațiu nou" : undefined });
    if (!choice) return;
    setImportingAll(true);
    const tid = toast.loading("Se importă backup-ul complet…");
    try {
      const { data } = await api.post("/import-all", {
        map: parsed.map,
        categories: parsed.categories,
        elements: parsed.elements,
        background_image: parsed.background_image,
        space_id: activeSpaceId,
        as_new_space: choice === "alt",
        exported_at: parsed.exported_at,
      });
      await load(data.space_id);
      await loadBackups();
      const where = data.space_name ? ` în „${data.space_name}"` : "";
      toast.success(`Import reușit${where}: ${data.elements} elemente, ${data.categories} categorii`, { id: tid });
    } catch {
      toast.error("Import complet eșuat", { id: tid });
    }
    setImportingAll(false);
  };

  // ---- map image export (PNG / PDF) with legend + total consumption ----
  const [exportingMap, setExportingMap] = useState(false);
  const exportMapImage = async (format) => {
    if (elements.length === 0) { toast.error("Nu există elemente de exportat."); return; }
    setExportingMap(true);
    const tid = toast.loading(`Se generează ${format.toUpperCase()}…`);
    try {
      const opts = {
        backgroundUrl: map.background_path ? fileUrl(map.background_path) : null,
        ppm, categories, elements,
        showConsumption: true,
        title: map.event_name || "Eveniment",
        subtitle: [map.event_date, map.event_location].filter(Boolean).join(" · "),
        guardMode,
      };
      if (format === "pdf") await exportMapPDF(opts);
      else await exportMapPNG(opts);
      toast.success(`Hartă exportată (${format.toUpperCase()})`, { id: tid });
    } catch (err) {
      toast.error("Export eșuat. Verifică fundalul hărții.", { id: tid });
    } finally {
      setExportingMap(false);
    }
  };

  // normalize a name for matching: trim + collapse inner whitespace + lowercase
  // (CSV import helpers live in lib/csvImport.js)

  // step 1: parse CSV and show a confirmation summary (no changes yet)
  const onCsvSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { return; }
    try {
      const text = await f.text();
      const rows = parseCsv(text);
      if (rows.length === 0) { toast.error("CSV gol sau invalid."); }
      else {
        const { plan, addNames, updateNames, unchanged } = buildImportPlan(rows, elements, categories, placeCatId);
        setImportPreview({ plan, addNames, updateNames, unchanged });
      }
    } catch {
      toast.error("Import eșuat. Verifică formatul CSV.");
    }
    e.target.value = "";
  };

  // step 2: execute the plan after the user confirms
  const confirmImport = async () => {
    if (!importPreview) return;
    const snapshot = await createBackup(true); // pre-import snapshot for undo
    const createdByKey = {};
    let created = 0, updated = 0;
    for (const p of importPreview.plan) {
      if (p.action === "create") {
        const { data } = await api.post("/elements", p.body);
        created++;
        createdByKey[p.key] = data.id;
      } else if (p.action === "update") {
        const id = p.id || createdByKey[p.key];
        if (id) { await api.put(`/elements/${id}`, p.body); if (p.id) updated++; }
      }
      // "skip" -> unchanged, nothing to do
    }
    await load();
    setImportPreview(null);
    if (snapshot?.id && (created > 0 || updated > 0)) setLastImportBackup(snapshot.id);
    const skipped = importPreview.unchanged || 0;
    toast.success(`Import: ${created} adăugate, ${updated} modificate${skipped ? `, ${skipped} neschimbate` : ""}`);
  };

  // revert to the snapshot taken right before the last import
  const undoLastImport = async () => {
    if (!lastImportBackup) return;
    await restoreBackup(lastImportBackup);
    setLastImportBackup(null);
  };

  // remove duplicate elements (keep one per normalized name)
  const cleanupDuplicates = async () => {
    const seen = new Map();
    const toDelete = [];
    elements.forEach((el) => {
      const key = normName(el.name);
      if (seen.has(key)) toDelete.push(el);
      else seen.set(key, el);
    });
    await createBackup(true); // safety snapshot before deleting
    for (const el of toDelete) await api.delete(`/elements/${el.id}`);
    await load();
    setDupPreview(null);
    toast.success(`${toDelete.length} duplicate șterse`);
  };

  const openCleanup = () => {
    const seen = new Set();
    let dups = 0;
    const names = new Set();
    elements.forEach((el) => {
      const key = normName(el.name);
      if (seen.has(key)) { dups++; names.add(el.name); }
      else seen.add(key);
    });
    setDupPreview({ count: dups, names: [...names] });
  };

  // ---- rubber band consumption ----
  const onRubberBand = (ids) => {
    const idSet = new Set(ids);
    const hit = visibleElements.filter((el) => idSet.has(el.id));
    const total = hit.reduce((s, el) => s + (el.consumption || 0), 0);
    const totalNight = hit.reduce((s, el) => s + (el.consumption_night || 0), 0);
    setBandResult({ count: hit.length, total, totalNight, ids: hit.map((h) => h.id) });
  };

  const toolBtn = (id, label, icon) => (
    <button
      data-testid={`tool-${id}`}
      onClick={() => { setTool(id); if (id === "calibrate") setCalibPoints([]); }}
      className={`flex items-center gap-2 px-3 h-9 text-sm border transition-colors ${
        tool === id ? "bg-[#5E8A1E] text-white border-[#5E8A1E]" : "bg-white text-zinc-700 border-[#A8C97D] hover:border-[#5E8A1E]"
      }`}
    >
      {icon}{label}
    </button>
  );

  const catFilterDropdown = (idPrefix) => (
    <div className="relative">
      <button data-testid={`${idPrefix}-filter`} onClick={() => setCatMenuOpen((o) => !o)}
        className="w-full h-9 px-2 border border-[#A8C97D] rounded-none text-sm bg-white flex items-center justify-between gap-2 hover:border-[#5E8A1E]">
        <span className="truncate">
          {hiddenCats.length === 0
            ? "Toate categoriile"
            : `${filterCats.length - hiddenCats.length} din ${filterCats.length} categorii`}
        </span>
        <ChevronDown size={15} strokeWidth={1.5} className={`shrink-0 text-zinc-400 transition-transform ${catMenuOpen ? "rotate-180" : ""}`} />
      </button>
      {catMenuOpen && (
        <div data-testid="filter-menu" className="mt-1 border border-[#A8C97D] bg-white shadow-lg">
          <div className="flex items-center justify-between px-2 py-2 border-b border-zinc-100">
            <span className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">Tipuri afișate</span>
            <div className="flex gap-2">
              <button data-testid="filter-select-all" onClick={() => setHiddenCats([])}
                className="text-[11px] text-[#5E8A1E] hover:underline">Toate</button>
              <button data-testid="filter-select-none" onClick={() => setHiddenCats(filterCats.map((c) => c.id))}
                className="text-[11px] text-zinc-500 hover:underline">Niciuna</button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto no-scrollbar p-1">
            {filterCats.map((c) => {
              const checked = !hiddenCats.includes(c.id);
              return (
                <label key={c.id} data-testid={`filter-cat-${c.id}`}
                  className="flex items-center gap-2 px-1.5 py-1.5 hover:bg-zinc-50 cursor-pointer">
                  <Checkbox checked={checked}
                    onCheckedChange={(v) => setHiddenCats((prev) =>
                      v ? prev.filter((id) => id !== c.id) : [...prev, c.id])}
                  />
                  <span className="w-2.5 h-2.5 shrink-0" style={{ background: c.color || "#5E8A1E" }} />
                  <span className="text-sm truncate">{c.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const geoQualityView = (q) => {
    if (!q) return null;
    if (q.degenerate) return <p className="text-[11px] text-[#B45309]">Puncte prea apropiate — alege locuri mai depărtate.</p>;
    const meta = q.rating === "good"
      ? { label: "Foarte bună", cls: "bg-[#DCFCE7] text-[#166534]" }
      : q.rating === "ok"
      ? { label: "Acceptabilă", cls: "bg-[#FEF3C7] text-[#92400E]" }
      : { label: "Slabă — recomand recalibrare", cls: "bg-[#FEE2E2] text-[#991B1B]" };
    return (
      <div data-testid="geo-quality" className="space-y-1">
        <div className="flex items-center gap-2">
          <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>Potrivire: {meta.label}</span>
        </div>
        <p className="text-[11px] text-zinc-500 font-mono-data">
          Eroare max: {q.maxAbsM.toFixed(1)} m ({(q.maxRel * 100).toFixed(1)}%)
          {q.worstAccuracy != null && <> · precizie GPS ±{Math.round(q.worstAccuracy)} m</>}
        </p>
      </div>
    );
  };

  const headerBar = (
      <header className="relative h-14 shrink-0 flex items-center justify-between px-4 border-b border-black/30 bg-[#14161A]">
        <div className="flex items-center gap-2.5 z-10">
          <MapPin size={22} className="text-[#8BC53F] shrink-0" strokeWidth={1.5} />
          <div className="leading-tight">
            <h1 data-testid="brand-title" className="font-head text-base sm:text-lg tracking-tight font-bold text-white">
              {map.event_name || "Eveniment"}
            </h1>
            <p data-testid="brand-subtitle" className="text-[11px] text-zinc-400 -mt-0.5">
              {[map.event_date, map.event_location].filter(Boolean).join(" · ") || "—"}
            </p>
          </div>
        </div>
        <img src="/logo360.png" alt="360 Revolution" data-testid="header-logo"
          className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-9 pointer-events-none select-none" />
        <div className="flex items-center gap-2 z-10">
          {isAdmin && (
            <div className="flex border border-[#A8C97D]">
              <button data-testid="mode-view-btn" onClick={exitAdmin}
                className={`px-3 h-8 text-sm ${mode === "view" ? "bg-[#09090B] text-white" : "bg-white text-zinc-600"}`}>
                Vizualizare
              </button>
              <button data-testid="mode-admin-btn" onClick={() => { setMode("admin"); setTool("select"); setBandResult(null); }}
                className={`px-3 h-8 text-sm ${mode === "admin" ? "bg-[#5E8A1E] text-white" : "bg-white text-zinc-600"}`}>
                Editare
              </button>
            </div>
          )}
          {isAuthenticated ? (
            <>
              <span data-testid="role-badge"
                className={`h-8 px-2.5 flex items-center text-[11px] font-bold uppercase tracking-wider border ${
                  isAdmin
                    ? "bg-[#5E8A1E] text-white border-[#5E8A1E]"
                    : isTeam
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-zinc-100 text-zinc-600 border-[#A8C97D]"
                }`}>
                {isAdmin ? "Admin" : isTeam ? "Team" : "Public"}
              </span>
              <Button data-testid="logout-btn" onClick={handleLogout}
                className="h-8 rounded-none bg-white text-zinc-700 border border-[#A8C97D] hover:bg-zinc-100">
                <LogOut size={15} strokeWidth={1.5} className="mr-1" /> Ieșire
              </Button>
            </>
          ) : (
            <Button data-testid="open-login-btn" onClick={login}
              className="h-8 rounded-none bg-[#5E8A1E] hover:bg-[#42630F] text-white">
              <Lock size={15} strokeWidth={1.5} className="mr-1" /> Autentificare
            </Button>
          )}
        </div>
      </header>
  );

  const segSlider = (opts, value, onChange, testid, accent = "#5E8A1E") => (
    <div data-testid={testid} className="flex border border-[#A8C97D] rounded-none overflow-hidden">
      {opts.map(([val, label]) => {
        const active = value === val;
        return (
          <button key={val} data-testid={`${testid}-${val}`} onClick={() => onChange(val)}
            className="flex-1 h-8 inline-flex items-center justify-center text-[11px] font-medium transition-colors border-r border-[#C3DBA0] last:border-r-0"
            style={active ? { backgroundColor: accent, color: "#fff" } : { backgroundColor: "#fff", color: "#52525b" }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = "#f4f4f5"; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "#fff"; }}>
            {label}
          </button>
        );
      })}
    </div>
  );

  const spaceBreakdown = (accessor, unit, fmt = (v) => Math.round(v).toLocaleString()) => {
    if (!totals || spaces.length <= 1) return null;
    return (
      <div className="mt-1 pt-1 border-t border-[#C3DBA0] space-y-0.5" data-testid="section-space-breakdown">
        {totals.spaces.map((sp) => (
          <div key={sp.space_id} className="flex justify-between text-[10px]">
            <span className={`truncate max-w-[62%] ${sp.space_id === activeSpaceId ? "text-[#5E8A1E] font-semibold" : "text-zinc-500"}`}>{sp.name}</span>
            <span className="font-mono-data text-zinc-600">{fmt(accessor(sp))} {unit}</span>
          </div>
        ))}
        <div className="flex justify-between text-[10px] font-bold pt-0.5">
          <span className="text-zinc-700">TOTAL</span>
          <span className="font-mono-data text-[#5E8A1E]">{fmt(accessor(totals.grand))} {unit}</span>
        </div>
      </div>
    );
  };

  const viewInfoPanel = (
          <aside className="w-72 shrink-0 border-l border-[#C3DBA0] bg-white flex flex-col">
            <div className="h-9 shrink-0 flex items-center justify-between px-3 border-b border-zinc-100">
              <button data-testid="collapse-right-btn" onClick={() => setRightCollapsed(true)} title="Ascunde panoul" className="text-zinc-400 hover:text-[#5E8A1E]">
                <ChevronRight size={16} strokeWidth={2} />
              </button>
              <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">Detalii & consum</span>
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-3">
              {selected && (() => {
                const cat = categories.find((c) => c.id === selected.category_id);
                return (
                  <div data-testid="element-details-panel" className="border-2 border-[#5E8A1E] bg-[#5E8A1E]/5 p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-head text-base font-bold truncate">{selected.name}</div>
                        <div className="text-xs text-zinc-500 flex items-center gap-1">
                          <span className="w-2 h-2 inline-block" style={{ background: cat?.color || "#5E8A1E" }} />
                          {cat?.name || "—"}
                        </div>
                      </div>
                      <button data-testid="close-details-btn" onClick={() => setSelectedId(null)}
                        className="text-zinc-400 hover:text-[#E63946] shrink-0">✕</button>
                    </div>
                    {selected.kind === "fence" ? (
                      <div data-testid="details-fence-length" className="text-sm font-mono-data text-[#B91C1C] font-bold">
                        Lungime: {fenceLengthM(selected.points, ppm).toFixed(1)} m
                        <span className="text-xs text-zinc-500 font-normal ml-1">({selected.points?.length || 0} puncte)</span>
                      </div>
                    ) : selected.kind === "guard" ? (
                      <div data-testid="details-guard-agents" className="space-y-1">
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500">Agenți</div>
                        <div className="grid grid-cols-4 gap-1 text-center">
                          {[["setup", "Set-up"], ["day", "Zi"], ["event", "Event"], ["night", "Noapte"]].map(([k, t]) => (
                            <div key={k} className={`border p-1 ${guardMode === k ? "border-[#0EA5E9] bg-[#0EA5E9]/10" : "border-[#C3DBA0]"}`}>
                              <div className="font-mono-data text-lg font-bold text-[#0369A1]">{selected[`agents_${k}`] || 0}</div>
                              <div className="text-[10px] text-zinc-500">{t}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-1 text-xs font-mono-data text-zinc-600">
                          <span>Dim: {selected.length}×{selected.width} m</span>
                          {isTeam && <span>Consum: {selected.consumption} kW</span>}
                          {isTeam && <span className="flex items-center gap-1 col-span-2"><Moon size={12} className="text-[#1E40AF]" /> Noaptea: {selected.consumption_night || 0} kW</span>}
                        </div>
                        {isTeam && [["Mobilier", selected.mobilier], ["Branding", selected.branding], ["Altele", selected.altele]].map(([t, v]) => (
                          <div key={t}>
                            <div className="text-[10px] uppercase tracking-wide text-zinc-500">{t}</div>
                            <div data-testid={`details-${t.toLowerCase()}`} className="text-sm whitespace-pre-wrap text-zinc-800">{v ? v : "—"}</div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })()}

              {isTeam && (
              <>
              <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900 flex items-center gap-1">
                <Zap size={14} className="text-[#FFC300]" /> Consum selecție
              </h2>
              <div className="flex border border-[#A8C97D]">
                <button data-testid="viewdrag-pan-btn" onClick={() => setViewDrag("pan")}
                  className={`flex-1 h-8 text-xs flex items-center justify-center gap-1 ${viewDrag === "pan" ? "bg-[#5E8A1E] text-white" : "bg-white text-zinc-600"}`}>
                  <Move size={13} strokeWidth={1.5} /> Navighează
                </button>
                <button data-testid="viewdrag-select-btn" onClick={() => setViewDrag("select")}
                  className={`flex-1 h-8 text-xs flex items-center justify-center gap-1 ${viewDrag === "select" ? "bg-[#5E8A1E] text-white" : "bg-white text-zinc-600"}`}>
                  <SquareDashedMousePointer size={13} strokeWidth={1.5} /> Selectează
                </button>
              </div>
              <p className="text-xs text-zinc-500">
                {viewDrag === "select"
                  ? "Trage pe hartă pentru a selecta o zonă și a însuma consumul."
                  : "Trage pentru a deplasa harta. Scroll = zoom. Comută pe «Selectează» pentru a însuma consumul."}
              </p>
              {(viewDrag === "select" || bandResult) && (
              <div data-testid="consumption-sum-display" className="border border-[#C3DBA0] bg-zinc-50 p-4">
                {bandResult ? (
                  <>
                    <div className="font-mono-data text-3xl font-bold text-[#5E8A1E]">
                      {bandResult.total.toLocaleString()} kW
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mt-0.5">Consum zi</div>
                    <div data-testid="consumption-night-sum" className="font-mono-data text-2xl font-bold text-[#1E40AF] mt-2 flex items-center gap-1">
                      <Moon size={16} className="text-[#1E40AF]" /> {(bandResult.totalNight || 0).toLocaleString()} kW
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mt-0.5">Consum noaptea</div>
                    <div className="text-xs text-zinc-500 font-mono-data mt-2 pt-2 border-t border-[#C3DBA0]">
                      {bandResult.count} elemente selectate
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-zinc-400">Nicio selecție</div>
                )}
              </div>
              )}

              {(() => null)()}

              <div className="pt-2 border-t border-[#C3DBA0] space-y-1">
                <span className="text-xs uppercase tracking-wide text-zinc-500 font-semibold flex items-center gap-1"><Zap size={14} className="text-[#FFC300]" /> Consum electric</span>
                {(() => {
                  const mode = showMissingConsumption ? "missing" : showConsumptionNight ? "noapte" : showConsumption ? "zi" : "off";
                  const set = (m) => {
                    setShowConsumption(m === "zi");
                    setShowConsumptionNight(m === "noapte");
                    setShowMissingConsumption(m === "missing");
                  };
                  const opts = [["off", <EyeOff size={13} strokeWidth={2} />], ["zi", "Zi"], ["noapte", "Noapte"], ["missing", "TBD"]];
                  return segSlider(opts, mode, set, "consum-mode-slider", "#5E8A1E");
                })()}
                {showConsumption && (
                  <div data-testid="consumption-total-day" className="border border-[#C3DBA0] bg-zinc-50 p-3">
                    <div className="font-mono-data text-2xl font-bold text-[#5E8A1E]"><span className="animate-total-pulse">{totalConsumptionDay.toLocaleString()} kW</span></div>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mt-0.5">Total consum zi</div>
                  </div>
                )}
                {showConsumptionNight && (
                  <div data-testid="consumption-total-night" className="border border-[#C3DBA0] bg-zinc-50 p-3">
                    <div className="font-mono-data text-2xl font-bold text-[#1E40AF] flex items-center gap-1"><Moon size={16} className="text-[#1E40AF]" /> <span className="animate-total-pulse">{totalConsumptionNight.toLocaleString()} kW</span></div>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mt-0.5">Total consum noaptea</div>
                  </div>
                )}
                {showMissingConsumption && (
                  <div data-testid="missing-consumption-count" className="text-xs text-[#B45309] font-mono-data">
                    {missingConsumptionCount} element{missingConsumptionCount === 1 ? "" : "e"} fără consum comunicat
                  </div>
                )}
                {(showConsumption || showConsumptionNight) && spaceBreakdown((s) => (showConsumptionNight ? s.consum_noapte : s.consum_zi), "kW")}
              </div>

              <div className="space-y-1">
                <span className="text-xs uppercase tracking-wide text-zinc-500 font-semibold flex items-center gap-1"><Grid3x3 size={14} className="text-[#FF00FF]" /> Grilă de referință</span>
                {(() => {
                  const gmode = !showGrid ? "off" : gridStep === 1 ? "1" : "5";
                  const setG = (m) => { if (m === "off") setShowGrid(false); else { setShowGrid(true); setGridStep(m === "1" ? 1 : 5); } };
                  return segSlider([["off", <EyeOff size={13} strokeWidth={2} />], ["5", "5 m"], ["1", "1 m"]], gmode, setG, "grid-mode-slider", "#5E8A1E");
                })()}
                {showGrid && (
                  <button data-testid="grid-lock-toggle" onClick={() => setGridLocked((v) => !v)}
                    className={`w-full flex items-center justify-center gap-1.5 h-7 text-[11px] font-medium border transition-colors ${gridLocked ? "bg-[#FF00FF] text-white border-[#FF00FF]" : "bg-white text-zinc-600 border-[#A8C97D] hover:border-[#FF00FF]"}`}>
                    <Lock size={12} strokeWidth={2} /> {gridLocked ? "Fix pe ecran (nu se rotește)" : "Se rotește cu harta"}
                  </button>
                )}
                {showGrid && (
                  <div className="flex items-center gap-2 pt-0.5">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500 shrink-0">Shift</span>
                    <input type="range" data-testid="grid-shift-x" min={-gridStep} max={gridStep} step={0.1}
                      value={gridShiftX} onChange={(e) => setGridShiftX(parseFloat(e.target.value))}
                      className="flex-1 accent-[#5E8A1E] cursor-pointer" />
                    <span className="text-[10px] font-mono-data text-zinc-500 w-10 text-right">{gridShiftX.toFixed(1)}m</span>
                  </div>
                )}
              </div>

              <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900 flex items-center gap-1 pt-2 border-t border-[#C3DBA0]">
                <Fence size={14} className="text-[#FF0000]" /> Garduri
              </h2>
              {segSlider([["off", <EyeOff size={13} strokeWidth={2} />], ["large", "Mari"], ["small", "Mici"]], fenceMode, setFenceMode, "fence-mode-switch", "#5E8A1E")}
              {fenceMode !== "off" && (
              <div data-testid="fence-total-display" className="border border-[#C3DBA0] bg-zinc-50 p-4">
                {fenceMode === "large" ? (
                  <>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Fence size={12} className="text-[#FF0000]" /> Garduri mari</div>
                    <div className="font-mono-data text-3xl font-bold text-[#B91C1C]"><span key="fl" className="animate-total-pulse">{totalFenceMLarge.toFixed(1)} m</span></div>
                    <div className="text-[11px] text-zinc-500 font-mono-data">{fencesLarge.length} garduri</div>
                  </>
                ) : (
                  <>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Fence size={12} className="text-[#F97316]" /> Garduri mici</div>
                    <div className="font-mono-data text-3xl font-bold text-[#C2410C]"><span key="fs" className="animate-total-pulse">{totalFenceMSmall.toFixed(1)} m</span></div>
                    <div className="text-[11px] text-zinc-500 font-mono-data">{fencesSmall.length} garduri</div>
                  </>
                )}
              </div>
              )}

              {fenceMode !== "off" && spaceBreakdown((s) => (fenceMode === "large" ? s.fence_large_m : s.fence_small_m), "m", (v) => v.toFixed(1))}

              <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900 flex items-center gap-1 pt-2 border-t border-[#C3DBA0]">
                <Shield size={14} className="text-[#0EA5E9]" /> Agenți pază
              </h2>
              {segSlider([["off", <EyeOff size={13} strokeWidth={2} />], ["setup", "Set-up"], ["day", "Zi"], ["event", "Event"], ["night", "Noapte"]], guardMode, setGuardMode, "guard-mode-switch", "#5E8A1E")}
              {guardMode !== "off" && (
              <div data-testid="guard-total-display" className="border border-[#C3DBA0] bg-zinc-50 p-4">
                <div className="font-mono-data text-3xl font-bold text-[#0369A1]"><span key={guardMode} className="animate-total-pulse">{guardTotal(guardMode)}</span></div>
                <div className="text-xs text-zinc-500 font-mono-data mt-1">
                  agenți · {guardModeLabel[guardMode]} · {guardActive(guardMode)} puncte active
                </div>
              </div>
              )}
              {guardMode !== "off" && spaceBreakdown((s) => Number(s[`agents_${guardMode}`]) || 0, "agenți", (v) => String(Math.round(v)))}
              <Button data-testid="open-guard-schedule-btn" onClick={() => setGuardScheduleOpen(true)}
                variant="outline" className="w-full h-9 rounded-none border-[#0EA5E9] text-[#0369A1] hover:bg-[#0EA5E9]/10">
                <CalendarClock size={15} strokeWidth={1.5} className="mr-1" /> Program pază
              </Button>
              </>
              )}
            </div>
          </aside>
  );

  const leftPanel = (
        <aside className="w-80 shrink-0 border-r border-[#C3DBA0] bg-white flex flex-col">
          <div className="h-9 shrink-0 flex items-center justify-between px-3 border-b border-zinc-100">
            <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">{mode === "admin" ? "Editare" : "Vizualizare"}</span>
            <button data-testid="collapse-left-btn" onClick={() => setLeftCollapsed(true)} title="Ascunde panoul" className="text-zinc-400 hover:text-[#5E8A1E]">
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-5">
            {mode === "admin" ? (
              <>
                <section className="space-y-2">
                  <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900">Filtrează categorii</h2>
                  {catFilterDropdown("admin-mode")}
                </section>

                <section className="space-y-1">
                  <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900">
                    Elemente ({adminFiltered.length})
                  </h2>
                  <div className="relative">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input data-testid="admin-search-input" placeholder="Caută element…" value={adminSearch}
                      onChange={(e) => setAdminSearch(e.target.value)}
                      className="pl-7 h-9 rounded-none border-[#A8C97D] text-sm" />
                  </div>
                  <div className="space-y-1 max-h-60 overflow-y-auto no-scrollbar pt-1">
                    {adminFiltered.map((el) => {
                      const cat = categories.find((c) => c.id === el.category_id);
                      return (
                        <button key={el.id} data-testid={`admin-list-element-${el.id}`}
                          onClick={() => handleSelect(el.id)}
                          className={`w-full flex items-center justify-between gap-2 px-2 h-8 text-sm border ${
                            selectedId === el.id ? "border-[#5E8A1E]" : "border-[#C3DBA0]"
                          } hover:border-[#5E8A1E]`}>
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 shrink-0" style={{ background: el.kind === "fence" ? (el.fence_size === "small" ? "#F97316" : "#FF0000") : (cat?.color || "#5E8A1E") }} />
                            <span className="truncate">{el.name}</span>
                          </span>
                          <span className="font-mono-data text-xs text-zinc-500 shrink-0">
                            {el.kind === "fence" ? `${fenceLengthM(el.points, ppm).toFixed(1)} m` : el.kind === "guard" ? `${el[`agents_${guardMode}`] || 0} ag.` : `${el.consumption} kW`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>

                {selected && selected.kind === "fence" && (
                  <section className="space-y-2" data-testid="fence-panel">
                    <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900 flex items-center gap-1">
                      <Fence size={14} className={selected.fence_size === "small" ? "text-[#F97316]" : "text-[#FF0000]"} />
                      {selected.fence_size === "small" ? "Gard mic" : "Gard mare"}
                    </h2>
                    <label className="block text-xs text-zinc-500">Nume</label>
                    <Input data-testid="fence-name-input" value={selected.name}
                      onChange={(e) => changeSelected({ ...selected, name: e.target.value })}
                      onBlur={() => commitElement(selected.id)}
                      className="h-9 rounded-none border-[#A8C97D] text-sm" />
                    <div className="border border-[#C3DBA0] bg-zinc-50 p-3">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Lungime gard</div>
                      <div data-testid="fence-length" className="font-mono-data text-2xl font-bold text-[#B91C1C]">
                        {fenceLengthM(selected.points, ppm).toFixed(1)} m
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">{selected.points?.length || 0} puncte</div>
                    </div>
                    <p className="text-xs text-zinc-500">Trage de punctele roșii pe hartă pentru a modifica traseul.</p>
                    <Button data-testid="fence-delete-btn" onClick={deleteSelected}
                      className="w-full h-9 rounded-none bg-white text-[#E63946] border border-[#E63946] hover:bg-[#E63946] hover:text-white">
                      <Trash2 size={15} strokeWidth={1.5} className="mr-1" /> Șterge gardul
                    </Button>
                  </section>
                )}

                {selected && selected.kind === "guard" && (
                  <section className="space-y-2" data-testid="guard-panel-edit">
                    <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900 flex items-center gap-1">
                      <Shield size={14} className="text-[#0EA5E9]" /> Punct pază
                    </h2>
                    <label className="block text-xs text-zinc-500">Nume</label>
                    <Input data-testid="guard-name-input" value={selected.name}
                      onChange={(e) => changeSelected({ ...selected, name: e.target.value })}
                      onBlur={() => commitElement(selected.id)}
                      className="h-9 rounded-none border-[#A8C97D] text-sm" />
                    <div className="grid grid-cols-4 gap-2">
                      {[["agents_setup", "Set-up", "guard-edit-setup"], ["agents_day", "Zi", "guard-edit-day"], ["agents_event", "Event", "guard-edit-event"], ["agents_night", "Noapte", "guard-edit-night"]].map(([k, t, tid]) => (
                        <label key={k} className="flex flex-col gap-0.5">
                          <span className="text-[10px] uppercase tracking-wide text-zinc-500">{t}</span>
                          <input data-testid={tid} type="number" min="0" step="1" value={selected[k] ?? 0}
                            onChange={(e) => changeSelected({ ...selected, [k]: parseInt(e.target.value) || 0 })}
                            onBlur={() => commitElement(selected.id)}
                            className="h-8 px-2 border border-[#A8C97D] rounded-none text-sm font-mono-data focus:border-[#0EA5E9] focus:outline-none" />
                        </label>
                      ))}
                    </div>
                    <p className="text-[11px] text-zinc-500">Număr de agenți pe fiecare regim.</p>
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <Button data-testid="guard-duplicate-btn" onClick={duplicateSelected}
                        className="h-9 rounded-none bg-zinc-100 text-zinc-800 hover:bg-[#0EA5E9] hover:text-white border border-[#A8C97D]">
                        <Copy size={15} strokeWidth={1.5} className="mr-1" /> Copiază
                      </Button>
                      <Button data-testid="guard-delete-btn" onClick={deleteSelected}
                        className="h-9 rounded-none bg-white text-[#E63946] border border-[#E63946] hover:bg-[#E63946] hover:text-white">
                        <Trash2 size={15} strokeWidth={1.5} className="mr-1" /> Șterge
                      </Button>
                    </div>
                  </section>
                )}

                {selected && selected.kind !== "fence" && selected.kind !== "guard" && (
                  <section className="space-y-2">
                    <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900">Proprietăți element</h2>
                    <PropertyPanel
                      element={selected} categories={categories}
                      onChange={changeSelected} onCommit={() => commitElement(selected.id)}
                      onRotate={rotateSelected} onDuplicate={duplicateSelected} onDelete={deleteSelected}
                    />
                  </section>
                )}

                {/* Danger zone moved to the right-side panel (admin mode) */}
              </>
            ) : (
              <>
                <section className="space-y-2">
                  <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900">Filtrează</h2>
                  {catFilterDropdown("view-mode")}
                  <div className="relative">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input data-testid="view-search-input" placeholder="Caută după nume…" value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-7 h-9 rounded-none border-[#A8C97D] text-sm" />
                  </div>
                </section>

                <section className="space-y-1">
                  <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900">
                    Elemente ({visibleElements.length})
                  </h2>
                  <div className="space-y-1">
                    {visibleElements.map((el) => {
                      const cat = categories.find((c) => c.id === el.category_id);
                      return (
                        <button key={el.id} data-testid={`list-element-${el.id}`}
                          onClick={() => handleSelect(el.id)}
                          className={`w-full flex items-center justify-between gap-2 px-2 h-8 text-sm border ${
                            selectedId === el.id ? "border-[#5E8A1E]" : "border-[#C3DBA0]"
                          } hover:border-[#5E8A1E]`}>
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 shrink-0" style={{ background: el.kind === "fence" ? (el.fence_size === "small" ? "#F97316" : "#FF0000") : (cat?.color || "#5E8A1E") }} />
                            <span className="truncate">{el.name}</span>
                          </span>
                          {isTeam && (
                            <span className="font-mono-data text-xs text-zinc-500 shrink-0">
                              {el.kind === "fence" ? `${fenceLengthM(el.points, ppm).toFixed(1)} m` : el.kind === "guard" ? `${el[`agents_${guardMode}`] || 0} ag.` : `${el.consumption} kW`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </section>
              </>
            )}
          </div>
        </aside>
  );

  const rightAdminPanel = (
          <aside className="w-80 shrink-0 border-l border-[#C3DBA0] bg-white flex flex-col">
            <div className="h-9 shrink-0 flex items-center justify-between px-3 border-b border-zinc-100">
              <button data-testid="collapse-right-btn" onClick={() => setRightCollapsed(true)} title="Ascunde panoul" className="text-zinc-400 hover:text-[#5E8A1E]">
                <ChevronRight size={16} strokeWidth={2} />
              </button>
              <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">Administrare</span>
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-4">
              <section className="space-y-2">
                <h2 className="font-head text-sm font-bold uppercase tracking-wide text-zinc-900">Unelte</h2>
                <div className="flex flex-wrap gap-2">
                  {toolBtn("select", "Selectează", <MousePointer2 size={15} strokeWidth={1.5} />)}
                  <button data-testid="tool-place" onClick={openPlace}
                    className={`flex items-center gap-2 px-3 h-9 text-sm border transition-colors ${
                      tool === "place" ? "bg-[#5E8A1E] text-white border-[#5E8A1E]" : "bg-white text-zinc-700 border-[#A8C97D] hover:border-[#5E8A1E]"
                    }`}>
                    <PlusSquare size={15} strokeWidth={1.5} /> Amplasează
                  </button>
                  <button data-testid="tool-fence" onClick={() => { setTool("fence"); setSelectedId(null); }}
                    className={`flex items-center gap-2 px-3 h-9 text-sm border transition-colors ${
                      tool === "fence" ? "bg-[#FF0000] text-white border-[#FF0000]" : "bg-white text-zinc-700 border-[#A8C97D] hover:border-[#FF0000]"
                    }`}>
                    <Fence size={15} strokeWidth={1.5} /> Gard mare
                  </button>
                  <button data-testid="tool-fence-small" onClick={() => { setTool("fence_small"); setSelectedId(null); }}
                    className={`flex items-center gap-2 px-3 h-9 text-sm border transition-colors ${
                      tool === "fence_small" ? "bg-[#F97316] text-white border-[#F97316]" : "bg-white text-zinc-700 border-[#A8C97D] hover:border-[#F97316]"
                    }`}>
                    <Fence size={15} strokeWidth={1.5} /> Gard mic
                  </button>
                  <button data-testid="tool-guard" onClick={openGuardPlace}
                    className={`flex items-center gap-2 px-3 h-9 text-sm border transition-colors ${
                      tool === "guard" ? "bg-[#0EA5E9] text-white border-[#0EA5E9]" : "bg-white text-zinc-700 border-[#A8C97D] hover:border-[#0EA5E9]"
                    }`}>
                    <Shield size={15} strokeWidth={1.5} /> Punct pază
                  </button>
                </div>
                {tool === "fence" ? (
                  <p className="text-xs text-[#B91C1C] font-medium">Gard mare · Click pe hartă pentru puncte · dublu-click pentru a termina · Esc anulează.</p>
                ) : tool === "fence_small" ? (
                  <p className="text-xs text-[#C2410C] font-medium">Gard mic · Click pe hartă pentru puncte · dublu-click pentru a termina · Esc anulează.</p>
                ) : tool === "guard" ? (
                  <p className="text-xs text-[#0369A1] font-medium">Click pe hartă pentru a amplasa punctul de pază.</p>
                ) : (
                  <p className="text-xs text-zinc-500 font-mono-data">Scară: {ppm.toFixed(1)} px/m</p>
                )}
              </section>

              <div data-testid="fence-total-admin" className="border border-[#C3DBA0] bg-zinc-50 p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1">
                    <Fence size={14} className="text-[#FF0000]" /> Total garduri mari
                  </span>
                  <span data-testid="fence-total-large" className="font-mono-data text-lg font-bold text-[#B91C1C]">{totalFenceMLarge.toFixed(1)} m</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1">
                    <Fence size={14} className="text-[#F97316]" /> Total garduri mici
                  </span>
                  <span data-testid="fence-total-small" className="font-mono-data text-lg font-bold text-[#C2410C]">{totalFenceMSmall.toFixed(1)} m</span>
                </div>
              </div>

              {isSuperAdmin && (
                <Button data-testid="open-activity-log-btn" onClick={() => window.open("/log", "_blank", "noopener")}
                  className="w-full h-9 rounded-none bg-[#5E8A1E] text-white hover:bg-[#42630F]">
                  <ClipboardList size={15} strokeWidth={1.5} className="mr-1" /> Vizualizare log activitate
                </Button>
              )}

              <div className="space-y-1 border border-[#C3DBA0] bg-zinc-50 p-3">
                <span className="text-xs uppercase tracking-wide text-zinc-500 font-semibold flex items-center gap-1"><Grid3x3 size={14} className="text-[#FF00FF]" /> Grilă de referință</span>
                {(() => {
                  const gmode = !showGrid ? "off" : gridStep === 1 ? "1" : "5";
                  const setG = (m) => { if (m === "off") setShowGrid(false); else { setShowGrid(true); setGridStep(m === "1" ? 1 : 5); } };
                  return segSlider([["off", <EyeOff size={13} strokeWidth={2} />], ["5", "5 m"], ["1", "1 m"]], gmode, setG, "grid-mode-slider-admin", "#5E8A1E");
                })()}
                {showGrid && (
                  <button data-testid="grid-lock-toggle-admin" onClick={() => setGridLocked((v) => !v)}
                    className={`w-full flex items-center justify-center gap-1.5 h-7 text-[11px] font-medium border transition-colors ${gridLocked ? "bg-[#FF00FF] text-white border-[#FF00FF]" : "bg-white text-zinc-600 border-[#A8C97D] hover:border-[#FF00FF]"}`}>
                    <Lock size={12} strokeWidth={2} /> {gridLocked ? "Fix pe ecran (nu se rotește)" : "Se rotește cu harta"}
                  </button>
                )}
                {showGrid && (
                  <div className="flex items-center gap-2 pt-0.5">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500 shrink-0">Shift</span>
                    <input type="range" data-testid="grid-shift-x-admin" min={-gridStep} max={gridStep} step={0.1}
                      value={gridShiftX} onChange={(e) => setGridShiftX(parseFloat(e.target.value))}
                      className="flex-1 accent-[#5E8A1E] cursor-pointer" />
                    <span className="text-[10px] font-mono-data text-zinc-500 w-10 text-right">{gridShiftX.toFixed(1)}m</span>
                  </div>
                )}
              </div>
              {isSuperAdmin && (
              <section className="space-y-2 border border-[#7C3AED]/40 bg-[#7C3AED]/5 p-3">
                <h2 className="font-head text-sm font-bold uppercase tracking-wide text-[#7C3AED] flex items-center gap-1">
                  <Shield size={15} strokeWidth={2} /> Utilizatori
                </h2>
                <p className="text-[11px] text-zinc-500">Creează sau actualizează conturi (email, parolă, rol).</p>
                <Button data-testid="open-user-mgmt-btn" onClick={() => { setUserForm({ email: "", password: "", role: "team" }); setUserExists(null); setUserMgmtOpen(true); loadUsers(); }}
                  className="w-full h-9 rounded-none bg-white text-[#7C3AED] border border-[#7C3AED] hover:bg-[#7C3AED] hover:text-white">
                  <PlusSquare size={15} strokeWidth={1.5} className="mr-1" /> Gestionare useri
                </Button>
              </section>
              )}
              {isSuperAdmin && (
              <section className="space-y-3 border-2 border-[#E63946]/40 bg-[#E63946]/5 p-3">
                <div>
                  <h2 className="font-head text-sm font-bold uppercase tracking-wide text-[#E63946] flex items-center gap-1">
                    <AlertTriangle size={15} strokeWidth={2} /> Zonă periculoasă
                  </h2>
                  <p className="text-[11px] text-zinc-500 mt-1">Acțiuni care modifică structura hărții. Folosește cu atenție.</p>
                </div>

                <Button data-testid="open-categories-btn" onClick={() => setCategoriesOpen(true)}
                  className="w-full h-9 rounded-none bg-white text-zinc-800 border border-[#A8C97D] hover:bg-[#E63946] hover:text-white">
                  <Layers size={15} strokeWidth={1.5} className="mr-1" /> Categorii
                </Button>

                <input ref={fileInputRef} type="file" accept="image/*" onChange={onUploadFile} className="hidden" data-testid="bg-file-input" />
                <Button data-testid="open-background-btn" onClick={() => setBackgroundOpen(true)}
                  className="w-full h-9 rounded-none bg-white text-zinc-800 border border-[#A8C97D] hover:bg-[#E63946] hover:text-white">
                  <Upload size={15} strokeWidth={1.5} className="mr-1" /> Fundal hartă & scară
                </Button>

                <input ref={csvInputRef} type="file" accept=".csv,text/csv" onChange={onCsvSelected} className="hidden" data-testid="csv-file-input" />
                <div className="pt-1">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-1">Elemente (CSV)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button data-testid="export-csv-btn" onClick={exportCsv}
                      className="h-9 px-2 gap-1 text-xs whitespace-nowrap rounded-none bg-white text-zinc-800 border border-[#A8C97D] hover:bg-[#E63946] hover:text-white">
                      <Download size={14} strokeWidth={1.5} className="shrink-0" /> Export elemente
                    </Button>
                    <Button data-testid="import-csv-btn" onClick={() => csvInputRef.current?.click()}
                      className="h-9 px-2 gap-1 text-xs whitespace-nowrap rounded-none bg-white text-zinc-800 border border-[#A8C97D] hover:bg-[#E63946] hover:text-white">
                      <FileUp size={14} strokeWidth={1.5} className="shrink-0" /> Import elemente
                    </Button>
                  </div>
                </div>

                <div className="pt-1">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-1">Pază</p>
                  <Button data-testid="admin-open-guard-schedule-btn" onClick={() => setGuardScheduleOpen(true)}
                    className="w-full h-9 rounded-none bg-white text-[#0369A1] border border-[#0EA5E9] hover:bg-[#0EA5E9] hover:text-white">
                    <CalendarClock size={15} strokeWidth={1.5} className="mr-1" /> Program pază (tabel + CSV)
                  </Button>
                </div>

                <input ref={jsonInputRef} type="file" accept=".json,application/json" onChange={onJsonSelected} className="hidden" data-testid="json-file-input" />
                <div className="pt-1">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-1">Backup complet (hartă + garduri + pază + tot)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button data-testid="export-all-btn" onClick={exportAll} disabled={importingAll}
                      className="h-9 px-2 gap-1 text-xs whitespace-nowrap rounded-none bg-[#5E8A1E] text-white hover:bg-[#42630F] disabled:opacity-50">
                      <Download size={14} strokeWidth={1.5} className="shrink-0" /> Export complet
                    </Button>
                    <Button data-testid="import-all-btn" onClick={() => jsonInputRef.current?.click()} disabled={importingAll}
                      className="h-9 px-2 gap-1 text-xs whitespace-nowrap rounded-none bg-white text-[#5E8A1E] border border-[#5E8A1E] hover:bg-[#5E8A1E] hover:text-white disabled:opacity-50">
                      <FileUp size={14} strokeWidth={1.5} className="shrink-0" /> Import complet
                    </Button>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1">Fișier JSON portabil, cu imaginea hărții inclusă. Util pentru mutarea între proiecte.</p>
                </div>

                <div className="pt-1">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-1">Export hartă (cu legendă & consum)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button data-testid="export-map-pdf-btn" onClick={() => exportMapImage("pdf")} disabled={exportingMap}
                      className="h-9 rounded-none bg-white text-zinc-800 border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white disabled:opacity-50">
                      <FileText size={15} strokeWidth={1.5} className="mr-1" /> PDF
                    </Button>
                    <Button data-testid="export-map-png-btn" onClick={() => exportMapImage("png")} disabled={exportingMap}
                      className="h-9 rounded-none bg-white text-zinc-800 border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white disabled:opacity-50">
                      <ImageIcon size={15} strokeWidth={1.5} className="mr-1" /> PNG
                    </Button>
                  </div>
                </div>

                {lastImportBackup && (
                  <Button data-testid="undo-import-btn" onClick={undoLastImport}
                    className="w-full h-9 rounded-none bg-[#FFC300] text-[#09090B] hover:bg-[#e6b000] font-semibold">
                    <Undo2 size={15} strokeWidth={2} className="mr-1" /> Anulează ultimul import
                  </Button>
                )}

                <Button data-testid="save-backup-btn" onClick={() => createBackup()}
                  className="w-full h-9 rounded-none bg-[#09090B] text-white hover:bg-[#E63946]">
                  <Save size={15} strokeWidth={1.5} className="mr-1" /> Salvează backup
                </Button>
                <Button data-testid="cleanup-duplicates-btn" onClick={openCleanup}
                  className="w-full h-9 rounded-none bg-white text-zinc-800 border border-[#A8C97D] hover:bg-[#E63946] hover:text-white">
                  <Trash2 size={15} strokeWidth={1.5} className="mr-1" /> Curăță duplicate
                </Button>
                <p className="text-[11px] text-zinc-500">Backup automat la fiecare ieșire din Editare.</p>
                <div className="space-y-1 max-h-52 overflow-y-auto no-scrollbar">
                  {backups.length === 0 && (
                    <p className="text-sm text-zinc-400">Niciun backup încă.</p>
                  )}
                  {backups.map((b) => (
                    <div key={b.id} data-testid={`backup-row-${b.id}`}
                      className="flex items-center justify-between gap-2 border border-[#C3DBA0] bg-white px-2 h-9">
                      <div className="min-w-0">
                        <div className="text-xs font-mono-data truncate">{b.label}</div>
                        <div className="text-[10px] text-zinc-500">{b.element_count} elemente</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button data-testid={`load-backup-${b.id}`} onClick={() => restoreBackup(b.id)}
                          title="Încarcă acest backup"
                          className="flex items-center gap-1 px-2 h-7 text-xs border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white">
                          <RotateCcw size={13} strokeWidth={1.5} /> Load
                        </button>
                        <button data-testid={`delete-backup-${b.id}`} onClick={() => deleteBackup(b.id)}
                          className="text-zinc-400 hover:text-[#E63946]">
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-1 mt-1 border-t border-[#E63946]/20">
                  <section className="space-y-2 border border-[#A8C97D] bg-[#5E8A1E]/5 p-3">
                    <h2 className="font-head text-sm font-bold uppercase tracking-wide text-[#5E8A1E] flex items-center gap-1">
                      <MapPin size={15} strokeWidth={2} /> Proiect
                    </h2>
                    <div className="space-y-2">
                      <Input data-testid="project-name-input" value={projectForm.event_name}
                        onChange={(e) => setProjectForm((f) => ({ ...f, event_name: e.target.value }))}
                        placeholder="Nume eveniment" className="h-9 rounded-none" />
                      <Input data-testid="project-location-input" value={projectForm.event_location}
                        onChange={(e) => setProjectForm((f) => ({ ...f, event_location: e.target.value }))}
                        placeholder="Locație" className="h-9 rounded-none" />
                      <Input data-testid="project-date-input" value={projectForm.event_date}
                        onChange={(e) => setProjectForm((f) => ({ ...f, event_date: e.target.value }))}
                        placeholder="Dată (ex: 21–23 August 2026)" className="h-9 rounded-none" />
                      <Button data-testid="save-project-btn" onClick={saveProject} disabled={savingProject}
                        className="w-full h-9 rounded-none bg-[#5E8A1E] hover:bg-[#42630F] text-white disabled:opacity-50">
                        <Save size={15} strokeWidth={1.5} className="mr-1" /> {savingProject ? "Se salvează…" : "Salvează detalii"}
                      </Button>
                      <Button data-testid="add-space-btn" onClick={addSpace}
                        className="w-full h-9 rounded-none bg-white text-[#5E8A1E] border border-[#5E8A1E] hover:bg-[#5E8A1E] hover:text-white">
                        <PlusSquare size={15} strokeWidth={1.5} className="mr-1" /> Adaugă spațiu
                      </Button>
                      <Button data-testid="open-reset-project-btn" onClick={openReset}
                        className="w-full h-9 rounded-none bg-white text-[#E63946] border border-[#E63946] hover:bg-[#E63946] hover:text-white">
                        <RotateCcw size={15} strokeWidth={1.5} className="mr-1" /> Reset proiect (șterge tot)
                      </Button>
                    </div>
                  </section>
                </div>
              </section>
              )}
            </div>
          </aside>
  );

  if (authLoading) {
    return (
      <div data-testid="auth-splash" className="h-[100dvh] w-screen flex flex-col items-center justify-center gap-6 bg-[#14161A]">
        <img src="/logo360.png" alt="360 Revolution" className="h-12 sm:h-14 animate-pulse" />
        <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full w-1/2 bg-[#8BC53F] rounded-full animate-[loading-slide_1.1s_ease-in-out_infinite]" />
        </div>
        <p className="text-xs uppercase tracking-widest text-zinc-500">Se încarcă…</p>
      </div>
    );
  }

  if (isMobile) {
    return (
      <MobileView
        bg={bg} ppm={ppm} categories={categories} elements={elements}
        selected={selected} selectedId={selectedId} onSelect={setSelectedId}
        isTeam={isTeam} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} isAuthenticated={isAuthenticated} onLogin={login} onLogout={logout}
        geo={geo} showUserLoc={showUserLoc} toggleUserLoc={toggleUserLoc}
        userDot={userDot} geoCalibrated={geoCalibrated}
        geoCalibDraft={geoCalibDraft} geoPendingPixel={geoPendingPixel} geoCapturing={geoCapturing}
        geoDraftQuality={geoDraftQuality}
        startGeoCalib={startGeoCalib} onGeoCalibPoint={onGeoCalibPoint} captureGeoPoint={captureGeoPoint}
        saveGeoCalibration={saveGeoCalibration} cancelGeoCalib={cancelGeoCalib}
        tool={tool}
      />
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col bg-zinc-50">
      {/* Header */}
        {headerBar}

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        {!leftCollapsed && leftPanel}

        {/* Canvas */}
        <main className="flex-1 relative">
          {/* Space tabs — floating over the map, top-left (Google Sheets style). Switch by clicking a tab. */}
          <div data-testid="space-tabs" className={`absolute top-3 z-20 flex items-end gap-0.5 max-w-[70%] overflow-x-auto no-scrollbar ${leftCollapsed ? "left-14" : "left-3"}`}>
            {spaces.map((s) => {
              const active = s.id === activeSpaceId;
              const editing = editingSpaceId === s.id;
              return (
                <div key={s.id} data-testid={`space-tab-${s.id}`}
                  className={`shrink-0 flex items-center gap-1 h-8 pl-3 pr-2 rounded-t-md border-t border-l border-r cursor-pointer transition-colors shadow-sm ${active ? "bg-white border-[#5E8A1E] text-[#5E8A1E] font-semibold" : "bg-white/70 backdrop-blur border-[#C3DBA0] text-zinc-500 hover:bg-white"}`}
                  onClick={() => switchSpace(s.id)}>
                  {editing ? (
                    <input autoFocus data-testid="space-rename-input" value={editingName}
                      onChange={(e) => setEditingName(e.target.value)} onClick={(e) => e.stopPropagation()}
                      onBlur={() => renameSpace(s.id, editingName)}
                      onKeyDown={(e) => { if (e.key === "Enter") renameSpace(s.id, editingName); if (e.key === "Escape") setEditingSpaceId(null); }}
                      className="h-5 w-24 text-xs px-1 text-zinc-900 rounded-none border border-zinc-300" />
                  ) : (
                    <span className="text-xs whitespace-nowrap"
                      onDoubleClick={(e) => { if (isSuperAdmin) { e.stopPropagation(); setEditingSpaceId(s.id); setEditingName(s.name); } }}>
                      {s.name}
                    </span>
                  )}
                  {isSuperAdmin && active && !editing && (
                    <span className="flex items-center text-zinc-400">
                      <button data-testid={`space-move-left-${s.id}`} title="Mută stânga" onClick={(e) => { e.stopPropagation(); moveSpace(s.id, -1); }} className="w-4 h-5 flex items-center justify-center hover:text-[#5E8A1E]"><ChevronLeft size={12} /></button>
                      <button data-testid={`space-move-right-${s.id}`} title="Mută dreapta" onClick={(e) => { e.stopPropagation(); moveSpace(s.id, 1); }} className="w-4 h-5 flex items-center justify-center hover:text-[#5E8A1E]"><ChevronRight size={12} /></button>
                      {spaces.length > 1 && (
                        <button data-testid={`space-delete-${s.id}`} title="Șterge spațiu" onClick={(e) => { e.stopPropagation(); deleteSpace(s.id); }} className="w-4 h-5 flex items-center justify-center hover:text-[#E63946]"><Trash2 size={11} /></button>
                      )}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {leftCollapsed && (
            <button data-testid="expand-left-btn" onClick={() => setLeftCollapsed(false)} title="Arată panoul"
              className="absolute top-3 left-3 z-20 w-9 h-9 flex items-center justify-center bg-white border border-[#A8C97D] shadow hover:bg-[#5E8A1E] hover:text-white">
              <ChevronRight size={16} strokeWidth={2} />
            </button>
          )}
          {rightCollapsed && isTeam && (
            <button data-testid="expand-right-btn" onClick={() => setRightCollapsed(false)} title="Arată panoul"
              className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center bg-white border border-[#A8C97D] shadow hover:bg-[#5E8A1E] hover:text-white">
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
          )}
          <Canvas
            backgroundUrl={bg} ppm={ppm} categories={categories} elements={visibleElements}
            mode={mode} tool={tool} selectedId={selectedId} onSelect={setSelectedId} onElementClick={setSelectedId}
            onPlace={placeElement} onElementDrag={dragElement} onElementCommit={commitElement}
            calibPoints={calibPoints} onCalibPoint={addCalibPoint} onRubberBand={onRubberBand}
            highlightIds={bandResult?.ids || []} viewDrag={viewDrag}
            showLabelsOnly={showLabelsOnly} showConsumption={showConsumption} showConsumptionNight={showConsumptionNight}
            showMissingConsumption={showMissingConsumption} showGrid={showGrid} gridStep={gridStep} gridLocked={gridLocked} gridShiftX={gridShiftX}
            focusRequest={focusRequest}
            onFenceCommit={onFenceCommit} onFencePointsChange={onFencePointsChange}
            onFencePointsCommit={onFencePointsCommit} canSeeDetails={isTeam}
            guardMode={guardMode} onGuardPlace={placeGuard} labelLod={labelLod}
            showGuards={guardMode !== "off"} showFences={fenceMode !== "off"}
            userDot={userDot} geoCalibPoints={tool === "geocalib" ? geoCalibDraft : null} onGeoCalibPoint={onGeoCalibPoint}
          />

          {/* location controls (all tiers) */}
          <div className="absolute bottom-4 left-4 z-10 flex flex-col gap-1">
            <button data-testid="toggle-user-loc-btn" onClick={toggleUserLoc}
              disabled={!geoCalibrated}
              title={geoCalibrated ? "Arată locația mea (GPS)" : "GPS necalibrat — adminul trebuie să calibreze 3 puncte"}
              className={`flex items-center gap-2 px-3 h-9 border shadow backdrop-blur transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                showUserLoc ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white/90 text-zinc-700 border-[#A8C97D] hover:bg-[#2563EB] hover:text-white"
              }`}>
              <LocateFixed size={16} strokeWidth={2} />
              <span className="text-sm">{showUserLoc ? (geo.status === "searching" ? "Se caută GPS…" : "Locația mea") : "Locația mea"}</span>
            </button>
            {showUserLoc && userDot && (
              <button data-testid="center-on-me-btn" onClick={centerOnMe}
                className="flex items-center gap-2 px-3 h-9 bg-white/90 backdrop-blur border border-[#A8C97D] shadow text-zinc-700 hover:bg-[#5E8A1E] hover:text-white">
                <Navigation size={15} strokeWidth={2} /> <span className="text-sm">Centrează pe mine</span>
              </button>
            )}
            {showUserLoc && geo.status === "denied" && (
              <div data-testid="geo-denied-hint" className="max-w-[240px] bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] text-[11px] p-2">
                Accesul la locație e blocat. Atinge 🔒 din bara de adrese → Permisiuni → Locație → Permite, apoi apasă din nou.
              </div>
            )}
            {showUserLoc && geo.status === "active" && geo.coords && (
              <div className="text-[11px] text-zinc-600 bg-white/85 backdrop-blur px-2 py-1 border border-[#C3DBA0] font-mono-data">
                Precizie ±{Math.round(geo.coords.accuracy)} m
              </div>
            )}
          </div>

          {/* GPS calibration in progress panel */}
          {tool === "geocalib" && (
            <div data-testid="geo-calib-panel" className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 bg-white border-2 border-[#7C3AED] shadow-2xl p-3 w-[320px]">
              <div className="flex items-center gap-2 font-head text-sm font-bold text-[#7C3AED] mb-1">
                <Satellite size={15} strokeWidth={2} /> Calibrare GPS ({geoCalibDraft.length}/3)
              </div>
              <p className="text-[11px] text-zinc-500 mb-2">Atinge pe hartă locul unde te afli, apoi capturează GPS-ul. Repetă pentru 3 puncte răspândite.</p>
              {geoCalibDraft.length >= 3 && <div className="mb-2">{geoQualityView(geoDraftQuality)}</div>}
              <div className="flex gap-2">
                {geoCalibDraft.length >= 3 ? (
                  <Button data-testid="geo-calib-save-btn" onClick={saveGeoCalibration}
                    className="flex-1 h-9 rounded-none bg-[#7C3AED] text-white hover:bg-[#6b25d6]">
                    <Save size={15} strokeWidth={1.5} className="mr-1" /> Salvează calibrarea
                  </Button>
                ) : (
                  <div className="flex-1 text-[11px] text-zinc-500 flex items-center">Mai adaugă {3 - geoCalibDraft.length} punct(e)…</div>
                )}
                <Button data-testid="geo-calib-cancel-btn" onClick={cancelGeoCalib}
                  className="h-9 rounded-none bg-white text-zinc-700 border border-[#A8C97D] hover:bg-zinc-100">
                  Anulează
                </Button>
              </div>
            </div>
          )}
        </main>

        {/* Right panel: Danger Zone (admin mode) */}
        {mode === "admin" && !rightCollapsed && rightAdminPanel}

        {/* Right panel: element details + consumption (view mode, team/admin only) */}
        {mode === "view" && isTeam && !rightCollapsed && viewInfoPanel}
      </div>

      {placeOpen && mode === "admin" && (
        <PlacePanel
          config={placeConfig}
          categories={categories}
          onChange={setPlaceConfig}
          onClose={closePlace}
        />
      )}

      {guardOpen && mode === "admin" && (
        <GuardPanel
          config={guardConfig}
          onChange={setGuardConfig}
          onClose={closeGuardPlace}
        />
      )}

      <Dialog open={!!dupPreview} onOpenChange={(o) => { if (!o) setDupPreview(null); }}>
        <DialogContent className="sm:max-w-md rounded-none border-[#A8C97D]">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight flex items-center gap-2">
              <Trash2 size={18} strokeWidth={1.5} /> Curăță duplicate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2" data-testid="cleanup-summary">
            {dupPreview?.count > 0 ? (
              <>
                <p className="text-sm text-zinc-700">
                  Se vor șterge <span className="font-bold text-[#E63946]">{dupPreview.count}</span> elemente duplicate,
                  păstrând câte unul din fiecare nume.
                </p>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Nume cu duplicate</div>
                  <div className="text-xs text-zinc-700 max-h-32 overflow-y-auto no-scrollbar">{dupPreview.names.join(", ")}</div>
                </div>
                <p className="text-[11px] text-zinc-500">Se creează automat un backup înainte de ștergere.</p>
              </>
            ) : (
              <p className="text-sm text-zinc-600">Nu există elemente duplicate.</p>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="cleanup-cancel-btn" onClick={() => setDupPreview(null)}
              className="rounded-none bg-white text-zinc-700 border border-[#A8C97D] hover:bg-zinc-100">Anulează</Button>
            {dupPreview?.count > 0 && (
              <Button data-testid="cleanup-confirm-btn" onClick={cleanupDuplicates}
                className="rounded-none bg-[#E63946] hover:bg-red-700 text-white">Șterge duplicatele</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={guardScheduleOpen} onOpenChange={setGuardScheduleOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none border-[#A8C97D]" data-testid="guard-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight flex items-center gap-2">
              <CalendarClock size={18} className="text-[#0EA5E9]" /> Program pază
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto no-scrollbar">
            {guards.length === 0 ? (
              <p className="text-sm text-zinc-500 py-6 text-center">Niciun punct de pază amplasat.</p>
            ) : (
              <table className="w-full text-sm border-collapse" data-testid="guard-schedule-table">
                <thead>
                  <tr className="border-b-2 border-[#A8C97D] text-left">
                    <th className="py-2 pr-2 font-semibold text-zinc-700">Punct pază</th>
                    {guardShifts.map(([, label]) => (
                      <th key={label} className="py-2 px-2 font-semibold text-zinc-700 text-center w-16">{label}</th>
                    ))}
                    <th className="py-2 pl-2 font-semibold text-zinc-900 text-center w-16">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {guards.map((g) => {
                    const vals = guardShifts.map(([k]) => Number(g[k]) || 0);
                    const rowTotal = vals.reduce((s, v) => s + v, 0);
                    return (
                      <tr key={g.id} data-testid={`guard-schedule-row-${g.id}`} className="border-b border-zinc-100">
                        <td className="py-1.5 pr-2 text-zinc-800">{g.name}</td>
                        {vals.map((v, i) => (
                          <td key={i} className="py-1.5 px-2 text-center font-mono-data text-zinc-600">{v || "–"}</td>
                        ))}
                        <td className="py-1.5 pl-2 text-center font-mono-data font-bold text-[#0369A1]">{rowTotal}</td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-[#A8C97D] bg-zinc-50">
                    <td className="py-2 pr-2 font-bold text-zinc-900">TOTAL</td>
                    {guardShifts.map(([k]) => {
                      const m = k.replace("agents_", "");
                      return <td key={k} className="py-2 px-2 text-center font-mono-data font-bold text-[#0EA5E9]">{guardTotal(m)}</td>;
                    })}
                    <td className="py-2 pl-2 text-center font-mono-data font-bold text-[#0369A1]">
                      {["setup", "day", "event", "night"].reduce((s, m) => s + guardTotal(m), 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="guard-schedule-close-btn" onClick={() => setGuardScheduleOpen(false)}
              className="rounded-none bg-white text-zinc-700 border border-[#A8C97D] hover:bg-zinc-100">Închide</Button>
            <Button data-testid="guard-schedule-export-btn" onClick={exportGuardScheduleCsv} disabled={guards.length === 0}
              className="rounded-none bg-[#0EA5E9] hover:bg-[#0369A1] text-white">
              <Download size={15} strokeWidth={1.5} className="mr-1" /> Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={(o) => { if (!resetting) setResetOpen(o); }}>
        <DialogContent className="sm:max-w-md rounded-none border-2 border-[#E63946]" data-testid="reset-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight flex items-center gap-2 text-[#E63946]">
              <AlertTriangle size={18} /> Reset proiect
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border-2 border-[#E63946]/40 bg-[#E63946]/5 p-3 text-xs text-[#7f1d1d] space-y-1">
              <p className="font-bold uppercase tracking-wide">Atenție — acțiune ireversibilă!</p>
              <p>Se șterg <b>toate elementele, gardurile, paza, fundalul hărții, calibrarea GPS și jurnalul de activitate</b>. Categoriile se păstrează. Nu se poate anula.</p>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold mb-1">1. Backup complet (obligatoriu)</p>
              <Button data-testid="reset-backup-btn" onClick={downloadFullBackupForReset}
                className={`w-full h-9 rounded-none ${resetBackupDone ? "bg-[#5E8A1E] text-white" : "bg-white text-[#5E8A1E] border border-[#5E8A1E] hover:bg-[#5E8A1E] hover:text-white"}`}>
                <Download size={15} strokeWidth={1.5} className="mr-1" /> {resetBackupDone ? "Backup descărcat ✓" : "Descarcă backup complet (JSON)"}
              </Button>
              <p className="text-[10px] text-zinc-400 mt-1">Recomandat: exportă separat și CSV / PDF din panou înainte de reset.</p>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold mb-1">2. Detalii proiect nou</p>
              <div className="space-y-2">
                <Input data-testid="reset-name-input" value={resetForm.event_name} onChange={(e) => setResetForm((f) => ({ ...f, event_name: e.target.value }))} placeholder="Nume eveniment nou" className="h-9 rounded-none" />
                <Input data-testid="reset-location-input" value={resetForm.event_location} onChange={(e) => setResetForm((f) => ({ ...f, event_location: e.target.value }))} placeholder="Locație" className="h-9 rounded-none" />
                <Input data-testid="reset-date-input" value={resetForm.event_date} onChange={(e) => setResetForm((f) => ({ ...f, event_date: e.target.value }))} placeholder="Dată" className="h-9 rounded-none" />
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold mb-1">3. Confirmare — scrie <b>RESET</b></p>
              <Input data-testid="reset-confirm-input" value={resetConfirmText} onChange={(e) => setResetConfirmText(e.target.value)} placeholder="RESET" className="h-9 rounded-none font-mono-data" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="reset-cancel-btn" onClick={() => setResetOpen(false)} disabled={resetting}
              className="rounded-none bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-100">Anulează</Button>
            <Button data-testid="reset-confirm-btn" onClick={doReset}
              disabled={resetting || !resetBackupDone || resetConfirmText.trim().toUpperCase() !== "RESET" || !resetForm.event_name.trim()}
              className="rounded-none bg-[#E63946] hover:bg-[#b91c1c] text-white disabled:opacity-40">
              <Trash2 size={15} strokeWidth={1.5} className="mr-1" /> {resetting ? "Se resetează…" : "Șterge tot și creează proiect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={userMgmtOpen} onOpenChange={setUserMgmtOpen}>
        <DialogContent className="sm:max-w-md rounded-none border-[#A8C97D]" data-testid="user-mgmt-dialog">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight flex items-center gap-2">
              <Shield size={18} className="text-[#7C3AED]" /> Gestionare useri
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border border-[#C3DBA0] bg-zinc-50">
              <div className="flex items-center justify-between px-3 h-8 border-b border-[#C3DBA0]">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Useri existenți</span>
                <button data-testid="users-refresh-btn" onClick={loadUsers} title="Reîncarcă" className="text-zinc-400 hover:text-[#7C3AED]">
                  <RotateCcw size={13} strokeWidth={2} />
                </button>
              </div>
              {Array.isArray(usersList) && usersList.length > 4 && (
                <div className="px-2 py-1.5 border-b border-[#C3DBA0]">
                  <Input data-testid="users-search-input" value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Caută după email…" className="h-7 text-xs rounded-none" />
                </div>
              )}
              <div className="max-h-48 overflow-y-auto no-scrollbar" data-testid="users-list">
                {usersList === null ? (
                  <div className="px-3 py-4 text-xs text-zinc-400 text-center">Se încarcă…</div>
                ) : usersError ? (
                  <div className="px-3 py-4 text-xs text-[#B45309] text-center">{usersError}</div>
                ) : usersList.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-zinc-400 text-center">Niciun user.</div>
                ) : (
                  (() => {
                    const q = userSearch.trim().toLowerCase();
                    const shown = q ? usersList.filter((u) => (u.email || "").toLowerCase().includes(q)) : usersList;
                    if (shown.length === 0) return <div className="px-3 py-4 text-xs text-zinc-400 text-center">Niciun rezultat.</div>;
                    return shown.map((u) => (
                      <div key={u.user_id} data-testid={`user-row-${u.email}`}
                        className="flex items-center gap-2 px-3 py-1.5 border-b border-zinc-100 last:border-b-0 hover:bg-white group">
                        <button onClick={() => { setUserForm((f) => ({ ...f, email: u.email })); setUserExists({ exists: true, roles: u.roles }); }}
                          className="flex-1 min-w-0 flex flex-col items-start text-left" title="Editează acest user">
                          <span className="truncate w-full text-xs text-zinc-800">{u.email}</span>
                          <span className="text-[9px] text-zinc-400 font-mono-data">Ultima autentificare: {fmtLastLogin(u.last_login)}</span>
                        </button>
                        <span className="flex gap-1 shrink-0">
                          {(u.roles || []).length === 0 ? (
                            <span className="text-[9px] px-1.5 py-0.5 bg-zinc-200 text-zinc-500 rounded">fără rol</span>
                          ) : u.roles.map((r) => (
                            <span key={r} className={`text-[9px] px-1.5 py-0.5 rounded text-white ${r === "superadmin" ? "bg-[#E63946]" : r === "admin" ? "bg-[#5E8A1E]" : "bg-[#0EA5E9]"}`}>{r}</span>
                          ))}
                        </span>
                        <button data-testid={`user-delete-${u.email}`} onClick={() => deleteUser(u)} title="Șterge userul"
                          className="shrink-0 w-6 h-6 flex items-center justify-center text-zinc-300 hover:text-[#E63946] hover:bg-[#E63946]/10">
                          <Trash2 size={13} strokeWidth={1.8} />
                        </button>
                      </div>
                    ));
                  })()
                )}
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold">Email</label>
              <Input data-testid="user-email-input" type="email" value={userForm.email}
                onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
                onBlur={checkUserExists}
                placeholder="nume@exemplu.ro" className="rounded-none mt-1" />
              {userExists?.exists && (
                <p data-testid="user-exists-warning" className="text-[11px] text-[#B45309] mt-1 flex items-start gap-1">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  Userul există deja (rol: {(userExists.roles || []).join(", ") || "—"}). Parola și rolul vor fi suprascrise.
                </p>
              )}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold">Parolă</label>
              <Input data-testid="user-password-input" type="text" value={userForm.password}
                onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="minim 8 caractere" className="rounded-none mt-1 font-mono-data" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold">Rol</label>
              <div className="flex border border-[#A8C97D] mt-1" data-testid="user-role-switch">
                {[["team", "Team"], ["admin", "Admin"]].map(([val, label]) => (
                  <button key={val} data-testid={`user-role-${val}`} onClick={() => setUserForm((f) => ({ ...f, role: val }))}
                    className={`flex-1 h-9 text-sm font-medium transition-colors ${userForm.role === val ? "bg-[#7C3AED] text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="user-cancel-btn" onClick={() => setUserMgmtOpen(false)}
              className="rounded-none bg-white text-zinc-700 border border-[#A8C97D] hover:bg-zinc-100">Anulează</Button>
            <Button data-testid="user-save-btn" onClick={submitUser} disabled={userBusy}
              className="rounded-none bg-[#7C3AED] hover:bg-[#6D28D9] text-white disabled:opacity-50">
              {userBusy ? "Se salvează…" : userExists?.exists ? "Suprascrie user" : "Creează user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <Dialog open={!!importPreview} onOpenChange={(o) => { if (!o) setImportPreview(null); }}>
        <DialogContent className="sm:max-w-md rounded-none border-[#A8C97D]">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight flex items-center gap-2">
              <FileUp size={18} strokeWidth={1.5} /> Confirmă importul
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2" data-testid="import-confirm-summary">
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-[#C3DBA0] bg-zinc-50 p-3 text-center">
                <div className="font-mono-data text-2xl font-bold text-[#10B981]">{importPreview?.addNames.length || 0}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wide">Adăugate</div>
              </div>
              <div className="border border-[#C3DBA0] bg-zinc-50 p-3 text-center">
                <div className="font-mono-data text-2xl font-bold text-[#5E8A1E]">{importPreview?.updateNames.length || 0}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wide">Modificate</div>
              </div>
            </div>
            {importPreview?.updateNames.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Se modifică</div>
                <div className="max-h-44 overflow-y-auto no-scrollbar space-y-1.5" data-testid="import-changes-list">
                  {importPreview.updateNames.map((u, i) => (
                    <div key={i} className="text-xs leading-snug">
                      <span className="font-semibold text-zinc-800">{u.name}</span>
                      {u.changes.length > 0 && (
                        <span className="text-zinc-500"> — {u.changes.join("; ")}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {importPreview?.unchanged > 0 && (
              <p className="text-[11px] text-zinc-500">{importPreview.unchanged} elemente neschimbate (vor fi ignorate).</p>
            )}
            <p className="text-[11px] text-zinc-500">Elementele care nu apar în CSV rămân neschimbate.</p>
          </div>
          <DialogFooter>
            <Button data-testid="import-cancel-btn" onClick={() => setImportPreview(null)}
              className="rounded-none bg-white text-zinc-700 border border-[#A8C97D] hover:bg-zinc-100">Anulează</Button>
            <Button data-testid="import-confirm-btn" onClick={confirmImport}
              className="rounded-none bg-[#5E8A1E] hover:bg-[#42630F] text-white">Confirmă importul</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={categoriesOpen} onOpenChange={setCategoriesOpen}>
        <DialogContent className="sm:max-w-lg rounded-none border-[#A8C97D] max-h-[85vh] overflow-y-auto no-scrollbar">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight flex items-center gap-2">
              <Layers size={18} strokeWidth={1.5} /> Categorii
            </DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            <CategoryManager
              categories={categories} onCreate={createCategory}
              onUpdate={updateCategory} onDelete={deleteCategory}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={backgroundOpen} onOpenChange={setBackgroundOpen}>
        <DialogContent className="sm:max-w-md rounded-none border-[#A8C97D]">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight flex items-center gap-2">
              <Upload size={18} strokeWidth={1.5} /> Fundal hartă & scară
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-zinc-500 font-bold">1. Imagine fundal</p>
              <Button data-testid="upload-bg-btn" onClick={() => fileInputRef.current?.click()}
                className="w-full h-10 rounded-none bg-zinc-100 text-zinc-800 border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white">
                <Upload size={15} strokeWidth={1.5} className="mr-1" /> Încarcă imagine fundal
              </Button>
              <p className="text-[11px] text-zinc-500">{map.background_path ? "Fundal încărcat." : "Niciun fundal încărcat."}</p>
            </div>
            <div className="space-y-2 border-t border-[#C3DBA0] pt-3">
              <p className="text-xs uppercase tracking-wide text-zinc-500 font-bold">2. Calibrare scară</p>
              <p className="text-xs text-zinc-500">Scară curentă: <span className="font-mono-data text-zinc-800">{ppm.toFixed(1)} px/m</span></p>
              <Button data-testid="calibrate-scale-btn"
                onClick={() => { setTool("calibrate"); setCalibPoints([]); setBackgroundOpen(false); toast.message("Click 2 puncte pe hartă pe o distanță cunoscută"); }}
                className="w-full h-10 rounded-none bg-zinc-100 text-zinc-800 border border-[#A8C97D] hover:bg-[#5E8A1E] hover:text-white">
                <Ruler size={15} strokeWidth={1.5} className="mr-1" /> Calibrează scara
              </Button>
              <p className="text-[11px] text-zinc-500">Trasezi o linie pe o distanță reală cunoscută și introduci lungimea în metri.</p>
            </div>
            <div className="space-y-2 border-t border-[#C3DBA0] pt-3">
              <p className="text-xs uppercase tracking-wide text-zinc-500 font-bold">3. Etichete elemente (LOD)</p>
              <p className="text-xs text-zinc-500">
                Prag afișare nume: <span className="font-mono-data text-zinc-800">{Math.round(map.label_lod || 26)} px</span>
              </p>
              <input data-testid="lod-slider" type="range" min="8" max="140" step="1"
                value={Math.round(map.label_lod || 26)}
                onChange={(e) => setLodLive(parseInt(e.target.value))}
                onMouseUp={persistLod} onTouchEnd={persistLod}
                className="w-full accent-[#5E8A1E]" />
              <div className="flex justify-between text-[10px] text-zinc-400 font-mono-data">
                <span>← mai devreme (mai multe nume)</span>
                <span>mai târziu →</span>
              </div>
              <p className="text-[11px] text-zinc-500">Controlează la ce nivel de zoom apar numele elementelor; sub prag se afișează doar un punct.</p>
            </div>
            <div className="space-y-2 border-t border-[#C3DBA0] pt-3">
              <p className="text-xs uppercase tracking-wide text-zinc-500 font-bold">4. Calibrare GPS (3 puncte)</p>
              <p className="text-xs text-zinc-500">
                Stare: <span className="font-mono-data text-zinc-800">{geoCalibrated ? `${(map.geo_points || []).length} puncte · calibrat` : "necalibrat"}</span>
              </p>
              {geoCalibrated && geoQualityView(geoQuality)}
              <Button data-testid="start-geo-calib-btn" onClick={startGeoCalib}
                className="w-full h-10 rounded-none bg-zinc-100 text-zinc-800 border border-[#A8C97D] hover:bg-[#7C3AED] hover:text-white">
                <Satellite size={15} strokeWidth={1.5} className="mr-1" /> {geoCalibrated ? "Recalibrează GPS" : "Începe calibrarea GPS"}
              </Button>
              <p className="text-[11px] text-zinc-500">Stând fizic pe teren, atingi pe hartă locul unde te afli și capturezi GPS-ul. 3 puncte răspândite dau cea mai bună precizie.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={calibOpen} onOpenChange={(o) => { if (!o) { setCalibOpen(false); setCalibPoints([]); } }}>
        <DialogContent className="sm:max-w-sm rounded-none border-[#A8C97D]">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight">Setează scara</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <p className="text-sm text-zinc-600">Care este lungimea reală a liniei trasate?</p>
            <div className="flex items-center gap-2">
              <Input data-testid="calib-meters-input" type="number" step="0.1" placeholder="ex: 10"
                value={calibMeters} onChange={(e) => setCalibMeters(e.target.value)}
                className="rounded-none border-[#A8C97D]" />
              <span className="text-sm font-mono-data text-zinc-500">metri</span>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="calib-confirm-btn" onClick={confirmCalibration}
              className="rounded-none bg-[#5E8A1E] hover:bg-[#42630F] text-white">Confirmă scara</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!geoPendingPixel} onOpenChange={(o) => { if (!o && !geoCapturing) setGeoPendingPixel(null); }}>
        <DialogContent className="sm:max-w-sm rounded-none border-[#A8C97D]">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight flex items-center gap-2">
              <Satellite size={18} strokeWidth={1.5} /> Capturează GPS — punct {geoCalibDraft.length + 1}/3
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-zinc-600">
              Stai fix în locul marcat pe hartă și apasă butonul. Recomandat: în aer liber, cu semnal bun.
            </p>
            {geo.status === "denied" && (
              <div className="bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] text-xs p-2">
                Accesul la locație e blocat. Reactivează-l din setările site-ului (🔒 în bara de adrese), apoi încearcă din nou.
              </div>
            )}
            <Button data-testid="geo-capture-btn" onClick={captureGeoPoint} disabled={geoCapturing}
              className="w-full h-11 rounded-none bg-[#7C3AED] text-white hover:bg-[#6b25d6] disabled:opacity-60">
              <LocateFixed size={16} strokeWidth={2} className="mr-1" />
              {geoCapturing ? "Se citește GPS-ul…" : "Capturează GPS aici"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
